import * as PIXI from 'pixi.js';
import { GameStateManager } from './GameState';
import { SPMapGenerator } from '../maps/SPMapGenerator';
import { Pathfinder } from '../maps/Pathfinder';
import { MazeBuilder } from '../systems/MazeBuilder';
import { TowerSystem } from '../systems/TowerSystem';
import { EnemySystem } from '../systems/EnemySystem';
import { WaveSystem } from '../systems/WaveSystem';
import { EconomySystem } from '../systems/EconomySystem';
import { RepairSystem } from '../systems/RepairSystem';
import { HUD } from '../ui/HUD';
import { BuildMenu } from '../ui/BuildMenu';
import { TowerInfoPanel } from '../ui/TowerInfoPanel';
import { WaveInfoPanel } from '../ui/WaveInfoPanel';
import { Instructions } from '../ui/Instructions';
import { LogMessages } from '../ui/LogMessages';
import { SettingsMenu } from '../ui/SettingsMenu';
import { GameSettings, loadSettings, saveSettings } from '../types/SettingsTypes';
import { Vector2 } from '../utils/Vector2';
import { Enemy, EnemyType, Tower, TowerType, Wall, Tile, TileType } from '../types/GameTypes';
import { GAME_CONSTANTS } from '../utils/Constants';
import { TOWER_STATS, UPGRADE_A_STATS, UPGRADE_B_STATS, UPGRADE_C_STATS } from '../types/TowerTypes';
import { AudioSystem } from '../assets/AudioSystem';
import { GPURenderer } from '../rendering/GPURenderer';
import { GraphicsPool } from '../utils/ObjectPool';
import { DamageNumbers } from '../ui/DamageNumbers';
import { MapDefinition } from '../editor/MapTypes';
import { DifficultyLevel, DIFFICULTY_LEVELS } from '../play/MapPlayScene';

export class Game {
  private app: PIXI.Application;
  private canvasElement: HTMLCanvasElement;
  private gameState: GameStateManager;
  private pathfinder: Pathfinder;
  private mazeBuilder: MazeBuilder;
  private towerSystem: TowerSystem;
  private enemySystem: EnemySystem;
  private waveSystem: WaveSystem;
  private economySystem: EconomySystem;
  
  private mapData: any;
  private gameContainer: PIXI.Container;
  private uiContainer: PIXI.Container;
  private mapGraphics: PIXI.Container; // Container for static map tiles
  private dynamicGraphics: PIXI.Container; // Container for towers and enemies
  private previewGraphics: PIXI.Container; // Container for ghost preview and grid
  private gridGraphics: PIXI.Graphics; // Grid overlay
  private ghostPreview: PIXI.Graphics | null = null; // Ghost preview of building
  private mousePosition: Vector2 = new Vector2(0, 0);
  private lastTime: number = 0;
  private isWaveActive: boolean = false;
  private expectedEnemyCount: number = 0; // Track how many enemies are expected to spawn
  private spawnedEnemyCount: number = 0; // Track how many enemies have actually spawned
  private autoWave: boolean = false;
  private autoWaveCountdownStarted: boolean = false;
  private isPaused: boolean = false; // Pause state (single player only)
  private gameSpeed: number = 1.0; // 1x, 2x, or 3x
  private gameTimer: number = 0; // Total game time in seconds
  private totalKills: number = 0;
  private totalDamage: number = 0;
  private selectedTower: Tower | null = null;
  private towerDamageMap: Map<string, number> = new Map(); // Track damage per tower
  private mvpIndicators: Map<string, PIXI.Container> = new Map(); // MVP visual indicators
  private selectedWall: Wall | null = null;
  private towerAttackSoundCounts: Map<string, number> = new Map(); // Track active attack sounds per tower type
  private deathSoundCount: number = 0; // Track active death sounds (max 3 simultaneous)
  private repairSystem: RepairSystem;
  private hud: HUD;
  private buildMenu: BuildMenu;
  private towerInfoPanel: TowerInfoPanel;
  private waveInfoPanel: WaveInfoPanel;
  private instructions: Instructions;
  private logMessages: LogMessages;
  private settingsMenu: SettingsMenu;
  private hotkeysPanel!: PIXI.Container;
  private audioSystem: AudioSystem;
  private cameraY: number = 0; // Camera pan offset (vertical)
  private cameraX: number = 0; // Camera pan offset (horizontal)
  private gameSettings: GameSettings;
  private buildMode: 'wall' | TowerType | null = null;
  private needsMapRedraw: boolean = true;
  private projectiles: Array<{ position: Vector2; target: Vector2; speed: number; lifetime: number; color: number; towerType: TowerType; aoeRadius?: number }> = [];
  private aoeEffects: Array<{ position: Vector2; radius: number; maxRadius: number; duration: number; maxDuration: number; color: number }> = [];
  private globalPath: Vector2[] = []; // Global path from spawn to goal (in pixel coordinates)
  private confirmDialog: PIXI.Container | null = null; // Confirmation dialog for restart
  private pauseOverlay: PIXI.Container | null = null; // Pause overlay (grey background + popup)
  private lastValidPath: Vector2[] = []; // Last valid path (shown in red when current path is blocked)
  private isPathValid: boolean = true; // Whether the current path is valid (not blocked)
  private blockingBuildings: Vector2[] = []; // All buildings that block the global path
  private lastPlacementTile: Vector2 | null = null;
  private blockedHighlightTile: Vector2 | null = null;
  private sessionStarted: boolean = false;
  private mainMenuRequestCallback?: () => void;
  private audioBootstrapped: boolean = false;
  
  // GPU Rendering
  private gpuRenderer: GPURenderer;
  private graphicsPool: GraphicsPool;
  
  // Damage numbers
  private damageNumbers: DamageNumbers;

  constructor(container: HTMLElement) {
    // Initialize systems first
    this.pathfinder = new Pathfinder();
    this.mazeBuilder = new MazeBuilder(this.pathfinder);
    this.towerSystem = new TowerSystem();
    this.enemySystem = new EnemySystem(this.pathfinder);
    this.waveSystem = new WaveSystem();
    this.gameState = new GameStateManager('sp', 1);
    this.economySystem = new EconomySystem(this.gameState.getEconomy().gold);
    this.repairSystem = new RepairSystem();
    this.gameSettings = loadSettings();
    this.hud = new HUD();
    this.hud.setQuickSellEnabled(this.gameSettings.quickSellEnabled ?? true);
    this.hud.setQuickBuildEnabled(this.gameSettings.quickBuildEnabled ?? true);
    this.buildMenu = new BuildMenu();
    this.towerInfoPanel = new TowerInfoPanel();
    this.towerInfoPanel.setRepairSystem(this.repairSystem);
    this.waveInfoPanel = new WaveInfoPanel();
    this.instructions = new Instructions({
      initialDontShowAgain: !this.gameSettings.showInstructionsOnStart,
      onDontShowAgainChange: (dontShow) => {
        this.gameSettings.showInstructionsOnStart = !dontShow;
        saveSettings(this.gameSettings);
      },
      onClose: () => {
        // Instructions dismissed; ensure flag stored
        saveSettings(this.gameSettings);
      },
      audioSystem: this.audioSystem,
    });
    this.instructions.hide();
    this.logMessages = new LogMessages();
    this.settingsMenu = new SettingsMenu();
    this.settingsMenu.onBackToMainMenu(() => {
      this.audioSystem?.playSound('button_click');
      this.hideSettings();
      this.handleBackToMainMenuRequest();
    });
    this.audioSystem = new AudioSystem();
    
    // Initialize GPU renderer and object pools
    this.gpuRenderer = new GPURenderer();
    this.graphicsPool = new GraphicsPool(100, 1000);
    console.log('[GPU] Initialized GPU renderer and object pools');
    
    // Initialize damage numbers
    this.damageNumbers = new DamageNumbers();

    this.gameContainer = new PIXI.Container();
    // Center the 20x20 map (640x640px) in 1920x1080 screen
    // Horizontal: (1920 - 640) / 2 = 640px
    // Vertical: (1080 - 640) / 2 = 220px
    this.gameContainer.x = 640;
    this.gameContainer.y = 220;
    this.mapGraphics = new PIXI.Container();
    this.dynamicGraphics = new PIXI.Container();
    this.previewGraphics = new PIXI.Container();
    this.gridGraphics = new PIXI.Graphics();
    this.gameContainer.addChild(this.mapGraphics);
    this.gameContainer.addChild(this.dynamicGraphics);
    // Add GPU containers after dynamic graphics (so they render on top)
    this.gameContainer.addChild(this.gpuRenderer.getEnemyContainer());
    this.gameContainer.addChild(this.gpuRenderer.getProjectileContainer());
    // Add damage numbers on top of everything else in game container
    this.gameContainer.addChild(this.damageNumbers.getContainer());
    this.gameContainer.addChild(this.previewGraphics);
    this.previewGraphics.addChild(this.gridGraphics);
    this.uiContainer = new PIXI.Container();

    // Initialize Pixi.js v7 - options passed directly to constructor
    this.app = new PIXI.Application({
      width: 1920,
      height: 1080,
      backgroundColor: 0x2a2a2a,
      antialias: false,
      resolution: 1,
    });
    
    console.log('Pixi.js initialized');
    // @ts-ignore - PIXI version compatibility
    console.log('Canvas:', this.app.canvas || this.app.view);
    console.log('Stage:', this.app.stage);
    
    // Use canvas or view depending on Pixi.js version
    // @ts-ignore - PIXI version compatibility
    this.canvasElement = (this.app.canvas || this.app.view) as HTMLCanvasElement;
    if (!this.canvasElement) {
      console.error('Canvas element not found!');
      return;
    }
    container.appendChild(this.canvasElement);
    console.log('Canvas appended to container');
    
    // Add containers to stage
    this.app.stage.addChild(this.gameContainer);
    this.app.stage.addChild(this.uiContainer);
    console.log('Containers added to stage');
    
    // Add UI
    this.uiContainer.addChild(this.hud.getContainer());
    this.uiContainer.addChild(this.buildMenu.getContainer());
    this.uiContainer.addChild(this.towerInfoPanel.getContainer());
    this.uiContainer.addChild(this.waveInfoPanel.getContainer());
    this.uiContainer.addChild(this.instructions.getContainer());
    this.uiContainer.addChild(this.logMessages.getContainer());
    this.uiContainer.addChild(this.settingsMenu.getContainer());
    
    // Create hotkeys info panel
    this.setupHotkeysPanel();
    
    console.log('UI added');
    
    // Setup build menu callback
    this.buildMenu.onBuild((type) => {
      if (type === null) {
        this.cancelBuildMode();
      } else {
        this.selectBuildType(type);
      }
    });

    // Setup start wave callback
    this.hud.onStartWave(() => {
      if (!this.autoWave) {
        if (!this.hasAnyTowersPlaced()) {
          this.warnNoTowers('start_wave');
          return;
        }
        this.audioSystem?.playSound('button_click');
        this.startNextWave(true);
      }
    });

    // Setup auto callback
    this.hud.onAuto(() => {
      if (!this.autoWave && !this.hasAnyTowersPlaced()) {
        this.warnNoTowers('auto_mode');
        return;
      }

      this.autoWave = !this.autoWave;
      this.hud.setAutoMode(this.autoWave);
      this.audioSystem?.playSound('button_click');
      if (this.autoWave && this.buildMode) {
        this.cancelBuildMode();
        this.logMessages.addMessage('Build mode cancelled: Auto mode active.');
      }
      if (this.autoWave && !this.isWaveActive) {
        // Start wave after 5 seconds when auto mode is enabled with countdown
        this.startWaveCountdown();
      }
    });

    // Setup speed callback
    this.hud.onSpeed(() => {
      this.gameSpeed = this.gameSpeed === 1.0 ? 2.0 : this.gameSpeed === 2.0 ? 3.0 : 1.0;
      this.hud.setSpeed(this.gameSpeed);
      this.app.ticker.speed = this.gameSpeed;
      // Play sound and update music speed
      if (this.audioSystem) {
        this.audioSystem.playSound('button_click');
        this.audioSystem.setMusicSpeed(this.gameSpeed);
      }
    });

    // Setup restart callback
    this.hud.onRestart(() => {
      this.audioSystem?.playSound('button_click');
      this.showRestartConfirmation();
    });

    // Setup test gold callback
    this.hud.onTestGold(() => {
      this.audioSystem?.playSound('button_click');
      this.addTestGold();
    });

    // Setup spawn callbacks
    this.hud.onSpawnF(() => {
      this.audioSystem?.playSound('button_click');
      this.spawnTestEnemies('flying', 10);
    });
    this.hud.onSpawnG(() => {
      this.audioSystem?.playSound('button_click');
      this.spawnTestEnemies('ghost', 10);
    });
    this.hud.onSpawnS(() => {
      this.audioSystem?.playSound('button_click');
      this.spawnTestEnemies('splitter', 20);
    });
    this.hud.onSpawnH(() => {
      this.audioSystem?.playSound('button_click');
      this.spawnTestEnemies('healer', 30);
    });
    // Setup spawn boss callback
    this.hud.onSpawnBoss(() => {
      this.audioSystem?.playSound('button_click');
      this.spawnTestBoss();
    });

    // Setup settings callback
    this.hud.onExit(() => {
      this.audioSystem?.playSound('button_click');
      this.handleBackToMainMenuRequest();
    });

    // Pause hotkey (P key) - single player only
    document.addEventListener('keydown', (e) => {
      if (e.code === 'KeyP' && this.gameState.getState().mode === 'sp') {
        // Only allow pause in single player mode
        if (!e.target || !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
          e.preventDefault();
          this.audioSystem?.playSound('button_click');
          this.togglePause();
        }
      }
    });

    // Setup ESC key handler (only when game is active)
    document.addEventListener('keydown', (e) => {
      if (e.code === 'Escape') {
        e.preventDefault();
        // Only handle ESC if canvas is visible (game is active)
        if (this.canvasElement && this.canvasElement.style.visibility === 'visible') {
          this.handleEscapeKey();
        }
      }
    });

    // Setup settings change callback
    this.settingsMenu.onSettingsChange((settings) => {
      this.gameSettings = settings;
      // Apply audio volume settings
      if (this.audioSystem) {
        this.audioSystem.setMasterVolume(settings.masterVolume);
        this.audioSystem.setMusicVolume(settings.musicVolume);
        this.audioSystem.setSfxVolume(settings.sfxVolume);
      }
      this.hud.setQuickSellEnabled(settings.quickSellEnabled ?? true);
      this.hud.setQuickBuildEnabled(settings.quickBuildEnabled ?? true);
      this.instructions.setDontShowAgain(!settings.showInstructionsOnStart);
    });

    // Setup upgrade callback
    this.towerInfoPanel.onSell((item) => {
      if (this.isPaused) {
        console.warn('Game is paused - cannot sell. Press P or ESC to unpause.');
        return;
      }
      // Check if it's a tower or wall by checking for 'type' property (towers have type, walls don't in the same way)
      if ('type' in item && (item as any).type && ['arrow', 'cannon', 'sniper', 'fire', 'ice', 'lightning'].includes((item as any).type)) {
        // It's a tower
        this.sellTower(item as Tower);
      } else {
        // It's a wall
        this.sellWall(item as Wall);
      }
    });

    // Setup repair callback
    this.towerInfoPanel.onRepair((item) => {
      if (this.isPaused) {
        console.warn('Game is paused - cannot repair. Press P or ESC to unpause.');
        return;
      }
      if (this.isWaveActive) {
        this.audioSystem?.playSound('invalid_placement');
        this.logMessages.addMessage('Cannot repair during an active wave.');
        return;
      }
      if (this.autoWave) {
        this.audioSystem?.playSound('invalid_placement');
        this.logMessages.addMessage('Cannot repair while Auto mode is enabled.');
        return;
      }
      this.startRepair(item);
    });

    // Setup error callback for disabled/locked buttons
    this.towerInfoPanel.onError((message) => {
      this.audioSystem?.playSound('invalid_placement');
      this.logMessages.addMessage(message);
    });

    this.towerInfoPanel.onUpgrade((tower, path) => {
      if (this.isPaused) {
        console.warn('Game is paused - cannot upgrade tower. Press P or ESC to unpause.');
        return;
      }
      
      const cost = path === 'A' 
        ? UPGRADE_A_STATS[tower.type].cost 
        : path === 'B'
          ? UPGRADE_B_STATS[tower.type].cost
          : (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper')
            ? UPGRADE_C_STATS[tower.type].cost
            : 0;
      
      // Check if upgrade is already owned
      if ((path === 'A' && (tower.hasUpgradeA || tower.level === 'B' || tower.level === 'C')) ||
          (path === 'B' && (tower.hasUpgradeB || tower.level === 'C')) ||
          (path === 'C' && tower.hasUpgradeC)) {
        if (this.towerInfoPanel.onErrorCallback) {
          this.towerInfoPanel.onErrorCallback('Upgrade already bought');
        }
        return;
      }
      
      // Check prerequisites
      if (path === 'B' && !tower.hasUpgradeA) {
        if (this.towerInfoPanel.onErrorCallback) {
          this.towerInfoPanel.onErrorCallback('Upgrade B requires Upgrade A first');
        }
        return;
      }
      
      if (path === 'C' && !tower.hasUpgradeB) {
        if (this.towerInfoPanel.onErrorCallback) {
          this.towerInfoPanel.onErrorCallback('Upgrade C requires Upgrade B first');
        }
        return;
      }
      
      if (this.economySystem.canAfford(cost)) {
        if (this.towerSystem.upgradeTower(tower, path)) {
          this.economySystem.spend(cost);
          this.audioSystem?.playSound(path === 'A' ? 'tower_upgrade_A' : path === 'B' ? 'tower_upgrade_B' : 'tower_upgrade_B');
          this.logMessages.addMessage(`${tower.type} tower upgraded to ${path} for ${cost}g`);
          console.log(`Tower upgraded - new level: ${tower.level}`);
          this.needsMapRedraw = true;
          this.renderMap();
          
          // Get updated tower reference from system
          const updatedTower = this.towerSystem.getTower(tower.id);
          if (updatedTower) {
            // Preserve selection - update selectedTower reference
            if (this.selectedTower && this.selectedTower.id === tower.id) {
              this.selectedTower = updatedTower; // Update reference to upgraded tower
              const canAffordA = this.economySystem.canAfford(UPGRADE_A_STATS[updatedTower.type].cost);
              const canAffordB = this.economySystem.canAfford(UPGRADE_B_STATS[updatedTower.type].cost);
              const canAffordC = (updatedTower.type === 'arrow' || updatedTower.type === 'cannon' || updatedTower.type === 'sniper')
                ? this.economySystem.canAfford(UPGRADE_C_STATS[updatedTower.type].cost)
                : false;
              this.towerInfoPanel.refresh(updatedTower, canAffordA, canAffordB, canAffordC, false);
            }
          }
        } else {
          // Upgrade failed - show error
          if (this.towerInfoPanel.onErrorCallback) {
            this.towerInfoPanel.onErrorCallback('Cannot upgrade tower');
          }
        }
      } else {
        // Can't afford - show error
        if (this.towerInfoPanel.onErrorCallback) {
          this.towerInfoPanel.onErrorCallback('Not enough gold');
        }
        this.logMessages.addMessage(`Not enough gold for upgrade (need ${cost}g)`);
      }
    });

    
    // Now initialize the game
    this.initialize();
  }

  private initialize(): void {
    console.log('Initializing game...');
    
    // Generate map (if not already loaded)
    if (!this.mapData) {
    this.mapData = SPMapGenerator.generate();
    console.log('Map generated:', this.mapData);
    
    const state = this.gameState.getState();
    state.grid = this.mapData.grid;
    state.spawns = this.mapData.spawns;
    state.goals = this.mapData.goals;
    
    console.log('Grid size:', state.grid.length, 'x', state.grid[0]?.length);
    console.log('Spawns:', state.spawns.length);
    console.log('Goals:', state.goals.length);
    
    // Calculate global path from spawn to goal
    this.updateGlobalPath();
    }

    // Start game loop
    this.app.ticker.speed = this.gameSpeed; // Initialize speed
    this.app.ticker.add(() => this.update(this.app.ticker.deltaMS / 1000));
    
    // Setup input
    this.setupInput();
    
    // Add start wave button (temporary - press Space to start)
    document.addEventListener('keydown', (e) => {
      if (e.code === 'Space' && !this.isWaveActive) {
        e.preventDefault();
        if (!this.hasAnyTowersPlaced()) {
          this.warnNoTowers('start_wave');
          return;
        }
        this.startNextWave(true);
      }
    });
    
    // Render initial map
    this.renderMap();
    
    // Initialize audio volumes from settings
    if (this.audioSystem) {
      this.audioSystem.setMasterVolume(this.gameSettings.masterVolume);
      this.audioSystem.setMusicVolume(this.gameSettings.musicVolume);
      this.audioSystem.setSfxVolume(this.gameSettings.sfxVolume);
    }
    
    console.log('Map rendered, game ready!');
  }

  private setupInput(): void {
    if (!this.canvasElement) {
      console.error('Canvas element not available for input setup');
      return;
    }
    
    // Mouse move tracking for ghost preview
    this.canvasElement.addEventListener('mousemove', (e) => {
      const rect = this.canvasElement.getBoundingClientRect();
      // Store screen coordinates (will be adjusted for camera in updateGhostPreview)
      this.mousePosition.x = e.clientX - rect.left;
      this.mousePosition.y = e.clientY - rect.top;
      this.updateGhostPreview();
    });
    
    // Left click
    this.canvasElement.addEventListener('click', async (e) => {
      // Don't start music or process clicks when paused
      if (this.isPaused) {
        console.warn('Game is paused - actions are disabled. Press P or ESC to unpause.');
        return;
      }
      
      // Initialize audio on first user interaction (required for browser autoplay policies)
      await this.ensureAudioReady();
      
      const rect = this.canvasElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Check if click is on UI elements (tower info panel, build menu, HUD, etc.)
      // If so, don't process as tile click to prevent deselection
      const towerInfoPanel = this.towerInfoPanel.getContainer();
      if (towerInfoPanel.visible) {
        const panelX = towerInfoPanel.x;
        const panelY = towerInfoPanel.y;
        // Panel width: ~220px (main panel) + 200px (buttons) = 420px
        // Panel height: ~170px (3 buttons at y=0, 60, 120, each 50px tall)
        const panelWidth = 420;
        const panelHeight = 170;
        if (x >= panelX && x <= panelX + panelWidth && y >= panelY && y <= panelY + panelHeight) {
          // Click is on tower info panel - don't process as tile click
          return;
        }
      }
      
      // Check if click is on build menu
      const buildMenuContainer = this.buildMenu.getContainer();
      if (buildMenuContainer.visible) {
        const menuX = buildMenuContainer.x;
        const menuY = buildMenuContainer.y;
        // Build menu: buttons at x=10, width ~70px each, 4 buttons = ~280px
        // Height: ~70px per button
        const menuWidth = 300;
        const menuHeight = 100;
        if (x >= menuX && x <= menuX + menuWidth && y >= menuY && y <= menuY + menuHeight) {
          // Click is on build menu - don't process as tile click
          return;
        }
      }
      
      // Check if click is on HUD (top area with gold, wave info, buttons)
      const hudContainer = this.hud.getContainer();
      if (hudContainer.visible) {
        // HUD is typically at the top of the screen
        // Check if click is in top 100px of screen
        if (y < 100) {
          // Click is likely on HUD - don't process as tile click
          return;
        }
      }
      
      // Convert screen coordinates to game container coordinates
      // Game container is positioned at (640, 220) to center the map
      const containerX = x - 640; // Convert to game container space
      const containerY = y - 220; // Convert to game container space
      
      // Account for camera pan offset (camera pan is within game container)
      const worldX = containerX - this.cameraX;
      const worldY = containerY - this.cameraY;
      
      const tileX = Math.floor(worldX / 32);
      const tileY = Math.floor(worldY / 32);
      
      // Check if click is outside the 20x20 arena bounds
      const arenaSize = GAME_CONSTANTS.SP_ARENA_SIZE; // 20
      if (tileX < 0 || tileX >= arenaSize || tileY < 0 || tileY >= arenaSize) {
        // Click is outside arena - cancel build mode and deselect
        if (this.buildMode) {
          this.cancelBuildMode();
        }
        if (this.selectedTower || this.selectedWall) {
          this.selectedTower = null;
          this.selectedWall = null;
          this.towerInfoPanel.hide();
          this.needsMapRedraw = true;
          this.renderMap();
        }
        return;
      }
      
      this.handleTileClick(tileX, tileY);
    });
    
    // Right click to cancel build mode or sell building (if quick sell enabled)
    // Also prevent browser context menu
    this.canvasElement.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.isPaused) {
        console.warn('Game is paused - cannot cancel build mode. Press P or ESC to unpause.');
        return;
      }
      
      // Check if quick sell is enabled and we're not in build/selection mode
      if (this.hud.isQuickSellEnabled() && !this.buildMode && !this.selectedTower && !this.selectedWall) {
        // Get tile coordinates from mouse position
        const rect = this.canvasElement.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        // Convert screen coordinates to game container coordinates
        const containerX = x - 640; // Convert to game container space
        const containerY = y - 220; // Convert to game container space
        // Account for camera pan offset
        const worldX = containerX - this.cameraX;
        const worldY = containerY - this.cameraY;
        const tileX = Math.floor(worldX / 32);
        const tileY = Math.floor(worldY / 32);
        
        // Try to sell tower or wall at this tile
        const state = this.gameState.getState();
        if (tileY >= 0 && tileY < state.grid.length && 
            tileX >= 0 && tileX < state.grid[tileY].length) {
          const tile = state.grid[tileY][tileX];
          if (tile.type === 'tower') {
            const tower = state.towers.find((t: Tower) => 
              Math.floor(t.position.x) === tileX && Math.floor(t.position.y) === tileY
            );
            if (tower) {
              this.sellTower(tower);
              return; // Don't cancel build mode if we sold something
            }
          } else if (tile.type === 'wall') {
            const wall = state.walls.find((w: Wall) => 
              Math.floor(w.position.x) === tileX && Math.floor(w.position.y) === tileY
            );
            if (wall) {
              this.sellWall(wall);
              return; // Don't cancel build mode if we sold something
            }
          }
        }
      }
      
      // Default: cancel build mode
      this.cancelBuildMode();
    });

    // Mouse wheel for panning (vertical and horizontal)
    this.canvasElement.addEventListener('wheel', (e) => {
      if (!this.gameSettings.mouseWheelPanEnabled) return;
      
      e.preventDefault(); // Prevent browser zoom
      
      // Vertical panning
      if (Math.abs(e.deltaY) > 0) {
        const deltaY = e.deltaY;
        const panAmountY = this.gameSettings.mouseWheelPanSpeed;
        const directionY = this.gameSettings.mouseWheelPanInverted ? -1 : 1;
        
        let newCameraY = this.cameraY + (deltaY > 0 ? panAmountY : -panAmountY) * directionY;
        newCameraY = Math.max(this.gameSettings.mouseWheelPanMinY, 
                             Math.min(this.gameSettings.mouseWheelPanMaxY, newCameraY));
        this.cameraY = newCameraY;
      }
      
      // Horizontal panning
      if (Math.abs(e.deltaX) > 0) {
        const deltaX = e.deltaX;
        const panAmountX = this.gameSettings.mouseWheelPanSpeedX;
        const directionX = this.gameSettings.mouseWheelPanInvertedX ? -1 : 1;
        
        let newCameraX = this.cameraX + (deltaX > 0 ? panAmountX : -panAmountX) * directionX;
        newCameraX = Math.max(this.gameSettings.mouseWheelPanMinX, 
                             Math.min(this.gameSettings.mouseWheelPanMaxX, newCameraX));
        this.cameraX = newCameraX;
      }
      
      // Apply camera position
      // Keep map centered, adjust for camera pan
      this.gameContainer.x = 640 + this.cameraX;
      this.gameContainer.y = 220 + this.cameraY;
    });

    // Mouse wheel for panning
    this.canvasElement.addEventListener('wheel', (e) => {
      if (!this.gameSettings.mouseWheelPanEnabled) return;
      
      e.preventDefault(); // Prevent browser zoom
      
      const delta = e.deltaY;
      const panAmount = this.gameSettings.mouseWheelPanSpeed;
      const direction = this.gameSettings.mouseWheelPanInverted ? -1 : 1;
      
      // Calculate new camera position
      let newCameraY = this.cameraY + (delta > 0 ? panAmount : -panAmount) * direction;
      
      // Apply limits
      newCameraY = Math.max(this.gameSettings.mouseWheelPanMinY, 
                           Math.min(this.gameSettings.mouseWheelPanMaxY, newCameraY));
      
      this.cameraY = newCameraY;
      this.gameContainer.y = 220 + this.cameraY;
    });

    // Hotkeys for building selection
    document.addEventListener('keydown', (e) => {
      // Only handle if not typing in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Block hotkeys when paused (except P and ESC which are handled elsewhere)
      if (this.isPaused && e.key !== 'p' && e.key !== 'P' && e.key !== 'Escape') {
        if (e.key === '1' || e.key === '2' || e.key === '3' || e.key === '4') {
          console.warn('Game is paused - cannot select build type. Press P or ESC to unpause.');
        }
        return;
      }

      switch (e.key) {
        case '1':
          this.selectBuildType('arrow');
          break;
        case '2':
          this.selectBuildType('cannon');
          break;
        case '3':
          this.selectBuildType('sniper');
          break;
        case '4':
          this.selectBuildType('wall');
          break;
      }
    });
  }

  public startSinglePlayerSession(): void {
    if (!this.sessionStarted) {
      this.sessionStarted = true;
      this.logMessages.addMessage('Single Player: build your maze and press Start Wave to begin.');
    }
    this.audioSystem?.setMusicSpeed(1.0);
    if (this.gameSettings.showInstructionsOnStart) {
      this.instructions.show();
      // Ensure instructions overlay renders on top
      this.uiContainer.removeChild(this.instructions.getContainer());
      this.uiContainer.addChild(this.instructions.getContainer());
    } else {
      this.instructions.hide();
    }
  }

  public getAudioSystem(): AudioSystem | null {
    return this.audioSystem ?? null;
  }

  public getApp(): PIXI.Application {
    return this.app;
  }

  public setCanvasVisible(visible: boolean): void {
    if (this.canvasElement) {
      this.canvasElement.style.visibility = visible ? 'visible' : 'hidden';
      this.canvasElement.style.pointerEvents = visible ? 'auto' : 'none';
    }
    this.gameContainer.visible = visible;
    this.uiContainer.visible = visible;
  }

  public onMainMenuRequested(callback: () => void): void {
    this.mainMenuRequestCallback = callback;
  }

  public async ensureAudioReady(targetSpeed?: number): Promise<void> {
    if (!this.audioSystem) return;
    await this.audioSystem.initialize();
    const desiredSpeed = targetSpeed ?? this.gameSpeed;
    if (!this.audioBootstrapped) {
      await this.audioSystem.startMusic(desiredSpeed);
      this.audioBootstrapped = true;
    } else if (targetSpeed) {
      this.audioSystem.setMusicSpeed(desiredSpeed);
    }
  }

  private hasAnyTowersPlaced(): boolean {
    return this.towerSystem.getAllTowers().length > 0;
  }

  private warnNoTowers(reason: 'start_wave' | 'auto_mode'): void {
    const message = reason === 'start_wave'
      ? 'Cannot start wave: place at least one tower first.'
      : 'Cannot enable Auto mode: place at least one tower first.';
    this.audioSystem?.playSound('invalid_placement');
    this.logMessages.addMessage(message);
  }

  private disableAutoModeDueToNoTowers(): void {
    if (!this.autoWave) {
      return;
    }
    this.autoWave = false;
    this.autoWaveCountdownStarted = false;
    this.hud.setAutoMode(false);
    this.logMessages.addMessage('Auto mode disabled: place at least one tower before restarting.');
  }

  public showSettings(): void {
    // Settings should only be accessible from main menu now
    // This method is kept for compatibility but shouldn't be called from game
    console.warn('Settings should be accessed from main menu, not from game');
  }

  public hideSettings(): void {
    this.settingsMenu.hide();
  }

  public loadMapData(mapDef: MapDefinition, difficulty: DifficultyLevel = 1): void {
    // Convert MapDefinition to game format
    const grid: Tile[][] = [];
    for (let y = 0; y < mapDef.grid.length; y++) {
      grid[y] = [];
      for (let x = 0; x < mapDef.grid[y].length; x++) {
        const tileType: TileType = mapDef.grid[y][x] === 'wall' ? 'wall' : 'empty';
        grid[y][x] = { x, y, type: tileType };
      }
    }

    // Set spawn and goal tiles
    for (const spawn of mapDef.spawns) {
      if (grid[spawn.y] && grid[spawn.y][spawn.x]) {
        grid[spawn.y][spawn.x].type = 'spawn';
      }
    }
    if (mapDef.goal && grid[mapDef.goal.y] && grid[mapDef.goal.y][mapDef.goal.x]) {
      grid[mapDef.goal.y][mapDef.goal.x].type = 'goal';
    }

    // Convert spawns and goals to Vector2
    const spawns = mapDef.spawns.map(s => new Vector2(s.x, s.y));
    const goals = mapDef.goal ? [new Vector2(mapDef.goal.x, mapDef.goal.y)] : [];

    // Update game state
    const state = this.gameState.getState();
    state.grid = grid;
    state.spawns = spawns;
    state.goals = goals;

    // Apply difficulty scaling to wave system
    const difficultyInfo = DIFFICULTY_LEVELS[difficulty];
    const waveBonus = difficultyInfo.waveBonus;
    if (waveBonus > 0) {
      // Adjust starting wave to account for difficulty
      this.gameState.setWaveNumber(waveBonus);
      // Scale enemy stats based on equivalent wave level
      this.waveSystem.setBaseWave(waveBonus);
    } else {
      // Reset to wave 1 for tutorial
      this.gameState.setWaveNumber(1);
      this.waveSystem.setBaseWave(1);
    }

    // Store map data
    this.mapData = {
      worldWidth: grid[0]?.length || 20,
      worldHeight: grid.length || 20,
      arenaWidth: grid[0]?.length || 20,
      arenaHeight: grid.length || 20,
      spawns,
      goals,
      grid,
    };

    // Recalculate path
    this.pathfinder.clearCache();
    this.updateGlobalPath();

    // Redraw map
    this.needsMapRedraw = true;
    this.renderMap();

    console.log(`Loaded map: ${mapDef.name || 'Unnamed'} (Difficulty: ${difficultyInfo.name}, +${waveBonus} waves)`);
  }

  private handleBackToMainMenuRequest(): void {
    if (this.mainMenuRequestCallback) {
      this.mainMenuRequestCallback();
    }
  }

  private selectBuildType(type: TowerType | 'wall'): void {
    // Block build mode selection when paused
    if (this.isPaused) {
      console.warn('Game is paused - cannot select build type. Press P or ESC to unpause.');
      return;
    }

    // Allow tower build mode during waves if quick build is enabled (can only build on walls)
    if (this.isWaveActive) {
      if (type === 'wall') {
        // Walls cannot be built during waves
        this.audioSystem?.playSound('invalid_placement');
        this.logMessages.addMessage('Cannot build walls during an active wave.');
        return;
      } else {
        // Towers can only be built during waves with quick build enabled
        const quickBuildEnabled = this.hud.isQuickBuildEnabled();
        console.log(`[BUILD MODE] Wave active, tower type=${type}, quickBuildEnabled=${quickBuildEnabled}`);
        if (!quickBuildEnabled) {
          this.audioSystem?.playSound('invalid_placement');
          this.logMessages.addMessage('Cannot enter build mode during an active wave. Enable Quick Build in Settings to build towers on walls.');
          return;
        }
        // Tower build mode with quick build enabled - allow it (continue to build mode activation below)
        console.log(`[BUILD MODE] Allowing tower build mode during wave (quick build enabled)`);
      }
    }

    // Auto mode no longer blocks build mode
    
    // Close build mode if clicking the same type (toggle off)
    if (this.buildMode === type) {
      this.cancelBuildMode();
      return;
    }
    
    this.buildMode = type;
    this.buildMenu.selectType(type as any);
    
    // Show tower/wall stats when selecting build type
    if (type !== 'wall') {
      // Create a temporary tower for stats display
      const tempTower = this.towerSystem.createTower(type, new Vector2(0, 0));
      const canAffordA = this.economySystem.canAfford(UPGRADE_A_STATS[type].cost);
      const canAffordB = this.economySystem.canAfford(UPGRADE_B_STATS[type].cost);
      const canAffordC = (tempTower.type === 'arrow' || tempTower.type === 'cannon' || tempTower.type === 'sniper')
        ? this.economySystem.canAfford(UPGRADE_C_STATS[tempTower.type].cost)
        : false;
      this.towerInfoPanel.showTower(tempTower, canAffordA, canAffordB, canAffordC, true); // true = build mode
      // Remove the temp tower from the system
      this.towerSystem.removeTower(tempTower.id);
    } else {
      // Show wall info in build mode
      const canAfford = this.economySystem.canAfford(GAME_CONSTANTS.WALL_COST);
      this.towerInfoPanel.showWall(canAfford);
    }
    this.updateGhostPreview();
  }

  private handleTileClick(tileX: number, tileY: number): void {
    const state = this.gameState.getState();
    
    // Check if clicking on existing tower (for selection/upgrade)
    // Check towers first, before build mode logic
    // Tower positions are stored in tile coordinates, so compare directly
    const clickedTower = this.towerSystem.getAllTowers().find(t => {
      return t.position.x === tileX && t.position.y === tileY;
    });
    
    if (clickedTower) {
      this.selectedTower = clickedTower;
      this.selectedWall = null; // Clear wall selection
      console.log(`Selected tower: ${clickedTower.type} (Level: ${clickedTower.level}) at tile (${tileX}, ${tileY})`);
      
      // Show tower info panel with upgrade buttons (not in build mode)
      const canAffordA = this.economySystem.canAfford(UPGRADE_A_STATS[clickedTower.type].cost);
      const canAffordB = this.economySystem.canAfford(UPGRADE_B_STATS[clickedTower.type].cost);
      const canAffordC = (clickedTower.type === 'arrow' || clickedTower.type === 'cannon' || clickedTower.type === 'sniper')
        ? this.economySystem.canAfford(UPGRADE_C_STATS[clickedTower.type].cost)
        : false;
      this.towerInfoPanel.showTower(clickedTower, canAffordA, canAffordB, canAffordC, false); // false = not build mode, show buttons
      
      this.needsMapRedraw = true;
      this.renderMap();
      return; // Important: return early to prevent build mode from processing
    }
    
    // Check if clicking on existing wall (for selection/sell)
    // Wall positions are stored in tile coordinates, so compare directly
    const clickedWall = state.walls.find(w => {
      return Math.floor(w.position.x) === tileX && Math.floor(w.position.y) === tileY;
    });
    
    // Only select wall if NOT in tower build mode with quick build enabled
    const isTowerBuildMode = this.buildMode && this.buildMode !== 'wall';
    const shouldQuickBuild = isTowerBuildMode && this.hud.isQuickBuildEnabled() && clickedWall;
    
    if (clickedWall && !shouldQuickBuild) {
      this.selectedWall = clickedWall;
      this.selectedTower = null; // Clear tower selection
      console.log(`Selected wall at tile (${tileX}, ${tileY})`);
      
      // Show wall info panel with sell button
      this.towerInfoPanel.showWall(true, clickedWall); // Pass wall object for sell functionality
      
      this.needsMapRedraw = true;
      this.renderMap();
      return; // Important: return early to prevent build mode from processing
    }
    
    // Deselect if clicking empty space (but keep build mode if active)
      this.selectedTower = null;
    this.selectedWall = null;
    // Only hide panel if not in build mode
    if (!this.buildMode) {
      this.towerInfoPanel.hide();
    }
      this.needsMapRedraw = true;
      this.renderMap();
    
    // Check if building is allowed during wave (only for towers without quick build)
    if (this.buildMode && this.isWaveActive && this.buildMode !== 'wall') {
      // Allow tower building during waves only if quick build is enabled and there's a wall at target
      const wallAtPosition = state.walls.find(w => 
        Math.floor(w.position.x) === tileX && Math.floor(w.position.y) === tileY
      );
      if (!this.hud.isQuickBuildEnabled() || !wallAtPosition) {
        // No wall or quick build disabled - block building
        this.audioSystem?.playSound('invalid_placement');
        this.logMessages.addMessage('Cannot build towers during wave. Enable Quick Build to build on walls.');
        return;
      }
      // Quick build enabled and wall exists - continue to building logic below
    }
    
    // Check if any enemy is on this tile (prevent placing buildings over mobs)
    if (this.buildMode) {
      const enemies = this.enemySystem.getAllEnemies();
      for (const enemy of enemies) {
        const enemyTile = enemy.position.toTile(32);
        if (enemyTile.x === tileX && enemyTile.y === tileY) {
          this.audioSystem?.playSound('invalid_placement');
          this.logMessages.addMessage('Cannot place building: enemy present on tile');
          return; // Don't allow placement
        }
      }
    }
    
    if (this.buildMode === 'wall') {
      if (this.mazeBuilder.validatePlacement(
        tileX,
        tileY,
        'wall',
        state.grid,
        state.spawns,
        state.goals
      )) {
        const cost = GAME_CONSTANTS.WALL_COST;
        if (this.economySystem.canAfford(cost)) {
          this.mazeBuilder.placeStructure(tileX, tileY, 'wall', state.grid);
          
          // Create wall object and add to state
          const wall: Wall = {
            id: `wall_${Date.now()}_${Math.random()}`,
            position: new Vector2(tileX, tileY),
            hp: GAME_CONSTANTS.WALL_HP_BASE,
            maxHp: GAME_CONSTANTS.WALL_HP_BASE,
            level: 'base',
          };
          state.walls.push(wall);
          this.lastPlacementTile = new Vector2(tileX, tileY);
          
          this.economySystem.spend(cost);
          this.pathfinder.clearCache();
          this.audioSystem?.playSound('wall_place');
          
          // Handle enemies on or near the building tile
          // If enemy is ON the building tile, set them up for block breaker mode
          // If enemy is near but not on, clear their path to force recalculation
          for (const enemy of this.enemySystem.getAllEnemies()) {
            if (enemy.type !== 'ghost' && enemy.type !== 'flying') {
              const enemyTile = enemy.position.toTile(32);
              // Check if enemy is on the building tile
              const isOnBuilding = (enemyTile.x === tileX && enemyTile.y === tileY);
              // Check if enemy is adjacent to the building tile
              const distanceX = Math.abs(enemyTile.x - tileX);
              const distanceY = Math.abs(enemyTile.y - tileY);
              const isAdjacent = (distanceX <= 1 && distanceY <= 1 && !isOnBuilding);
              
              if (isOnBuilding) {
                // Enemy is ON the building - set up for block breaker mode
                const buildingTile = new Vector2(tileX, tileY);
                enemy.blockingBuildingTile = buildingTile;
                enemy.idleTime = 0;
                enemy.lastPosition = enemy.position.clone();
                console.log(`[BUILD] Enemy ${enemy.id} is on building tile (${tileX}, ${tileY}), will enter block breaker mode`);
              } else if (isAdjacent) {
                // Enemy is adjacent - clear path to force recalculation
                enemy.path = [];
                enemy.currentPathIndex = 0;
                enemy.idleTime = 0;
                enemy.lastPosition = enemy.position.clone();
              } else {
                // For enemies further away, check if their next waypoint is blocked
                if (enemy.path.length > 0 && enemy.currentPathIndex < enemy.path.length) {
                  const nextWaypoint = enemy.path[enemy.currentPathIndex];
                  const waypointTile = nextWaypoint.toTile(32);
                  if (waypointTile.x === tileX && waypointTile.y === tileY) {
                    // Next waypoint is blocked, clear path to force recalculation
                    enemy.path = [];
                    enemy.currentPathIndex = 0;
                    enemy.idleTime = 0;
                    enemy.lastPosition = enemy.position.clone();
                  }
                }
              }
            }
          }
          
          // Update global path (this will update blockingBuildings if path is blocked)
          this.updateGlobalPath();
          // Don't force repath - let enemies continue moving and update naturally
          this.needsMapRedraw = true;
          this.renderMap();
          // Keep build mode active until right click
          this.logMessages.addMessage(`Wall placed for ${cost}g`);
        } else {
          this.audioSystem?.playSound('invalid_placement');
          this.logMessages.addMessage(`Not enough gold for wall (need ${cost}g)`);
        }
      } else {
        this.audioSystem?.playSound('invalid_placement');
        this.logMessages.addMessage('Invalid wall placement');
      }
    } else if (this.buildMode && this.buildMode !== 'wall' as any) {
      // Quick Build: Check if there's a wall at this position
      const wallAtPosition = state.walls.find(w => 
        Math.floor(w.position.x) === tileX && Math.floor(w.position.y) === tileY
      );
      
      // Check tower cost BEFORE selling the wall
      const towerCost = TOWER_STATS[this.buildMode].cost;
      const currentGold = this.economySystem.getGold();
      const wallRefund = wallAtPosition ? Math.floor(GAME_CONSTANTS.WALL_COST * 0.5) : 0;
      const goldAfterRefund = currentGold + wallRefund;
      
      // If quick build is enabled and there's a wall, check if we can afford the tower
      if (wallAtPosition && this.hud.isQuickBuildEnabled()) {
        // Check if player can afford tower even with wall refund
        if (goldAfterRefund < towerCost) {
          // Can't afford tower - exit build mode and show message
          const towerType = this.buildMode; // Store before clearing
          this.buildMode = null;
          this.towerInfoPanel.hide();
          this.audioSystem?.playSound('invalid_placement');
          this.logMessages.addMessage(`Not enough gold for ${towerType} tower (need ${towerCost}g, have ${goldAfterRefund}g with refund)`);
          console.log(`[QUICK BUILD] Cannot afford tower after wall refund. Need: ${towerCost}g, Have: ${goldAfterRefund}g`);
          this.needsMapRedraw = true;
          this.renderMap();
          return; // Don't sell wall, exit build mode
        }
        
        // Can afford it - proceed with selling wall
        this.economySystem.addGold(wallRefund);
        
        // Remove wall from state
        const wallIndex = state.walls.findIndex(w => w.id === wallAtPosition.id);
        if (wallIndex !== -1) {
          state.walls.splice(wallIndex, 1);
        }
        
        // Remove wall from grid
        this.mazeBuilder.removeStructure(tileX, tileY, state.grid);
        
        // Play both gold gain sounds alternately for wall sell
        const useSound2 = Math.random() < 0.5;
        this.audioSystem?.playSound(useSound2 ? 'gold_gain_2' : 'gold_gain_1', 0.6);
        setTimeout(() => {
          this.audioSystem?.playSound(useSound2 ? 'gold_gain_1' : 'gold_gain_2', 0.6);
        }, 100);
        
        this.logMessages.addMessage(`Wall sold for ${wallRefund}g (Quick Build)`);
        console.log(`[QUICK BUILD] Sold wall at (${tileX}, ${tileY}) for ${wallRefund}g`);
      }
      
      // Place tower (validation will check if tile is now empty after wall removal)
      if (this.mazeBuilder.validatePlacement(
        tileX,
        tileY,
        'tower',
        state.grid,
        state.spawns,
        state.goals
      )) {
        // Check if path is blocked - don't allow placement if so
        if (!this.isPathValid) {
          this.audioSystem?.playSound('invalid_placement');
          this.logMessages.addMessage('Cannot place tower: path is blocked! Clear the path first.');
          console.log(`[BUILD] Cannot place tower - path is blocked (red line)`);
          return;
        }
        
        const cost = TOWER_STATS[this.buildMode].cost;
        if (this.economySystem.canAfford(cost)) {
          const position = new Vector2(tileX, tileY); // Store as tile coordinates
          const tower = this.towerSystem.createTower(this.buildMode, position);
          state.towers.push(tower);
          this.mazeBuilder.placeStructure(tileX, tileY, 'tower', state.grid);
          this.lastPlacementTile = new Vector2(tileX, tileY);
          this.economySystem.spend(cost);
          this.pathfinder.clearCache();
          this.audioSystem?.playSound('tower_place');
          
          // Handle enemies on or near the building tile
          // If enemy is ON the building tile, set them up for block breaker mode
          // If enemy is near but not on, clear their path to force recalculation
          for (const enemy of this.enemySystem.getAllEnemies()) {
            if (enemy.type !== 'ghost' && enemy.type !== 'flying') {
              const enemyTile = enemy.position.toTile(32);
              // Check if enemy is on the building tile
              const isOnBuilding = (enemyTile.x === tileX && enemyTile.y === tileY);
              // Check if enemy is adjacent to the building tile
              const distanceX = Math.abs(enemyTile.x - tileX);
              const distanceY = Math.abs(enemyTile.y - tileY);
              const isAdjacent = (distanceX <= 1 && distanceY <= 1 && !isOnBuilding);
              
              if (isOnBuilding) {
                // Enemy is ON the building - set up for block breaker mode
                const buildingTile = new Vector2(tileX, tileY);
                enemy.blockingBuildingTile = buildingTile;
                enemy.idleTime = 0;
                enemy.lastPosition = enemy.position.clone();
                console.log(`[BUILD] Enemy ${enemy.id} is on building tile (${tileX}, ${tileY}), will enter block breaker mode`);
              } else if (isAdjacent) {
                // Enemy is adjacent - clear path to force recalculation
                enemy.path = [];
                enemy.currentPathIndex = 0;
                enemy.idleTime = 0;
                enemy.lastPosition = enemy.position.clone();
              } else {
                // For enemies further away, check if their next waypoint is blocked
                if (enemy.path.length > 0 && enemy.currentPathIndex < enemy.path.length) {
                  const nextWaypoint = enemy.path[enemy.currentPathIndex];
                  const waypointTile = nextWaypoint.toTile(32);
                  if (waypointTile.x === tileX && waypointTile.y === tileY) {
                    // Next waypoint is blocked, clear path to force recalculation
                    enemy.path = [];
                    enemy.currentPathIndex = 0;
                    enemy.idleTime = 0;
                    enemy.lastPosition = enemy.position.clone();
                  }
                }
              }
            }
          }
          
          // Update global path (this will update blockingBuildings if path is blocked)
          this.updateGlobalPath();
          // Don't force repath - let enemies continue moving and update naturally
          this.needsMapRedraw = true;
          this.renderMap();
          // Keep build mode active until right click
          this.logMessages.addMessage(`${this.buildMode} tower placed for ${cost}g`);
        } else {
          this.audioSystem?.playSound('invalid_placement');
          this.logMessages.addMessage(`Not enough gold for ${this.buildMode} tower (need ${cost}g)`);
        }
      } else {
        this.audioSystem?.playSound('invalid_placement');
        this.logMessages.addMessage('Invalid tower placement');
      }
    }
  }

  private setupHotkeysPanel(): void {
    this.hotkeysPanel = new PIXI.Container();
    
    // Position on left, below Spawn Boss button (which ends at y=400)
    const screenHeight = this.app.screen.height;
    this.hotkeysPanel.x = 30;
    this.hotkeysPanel.y = 420; // Below Spawn Boss button (y=350, height=50, ends at y=400) with spacing
    
    // Background (transparent - no background)
    // Removed background for transparency
    
    // Title
    const title = new PIXI.Text('Hotkeys', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    title.x = 10;
    title.y = 10;
    this.hotkeysPanel.addChild(title);
    
    // Hotkeys list
    const hotkeys = [
      { key: '1', action: 'Arrow Tower' },
      { key: '2', action: 'Cannon Tower' },
      { key: '3', action: 'Sniper Tower' },
      { key: '4', action: 'Wall' },
      { key: 'Space', action: 'Start Wave' },
      { key: 'RC', action: 'Cancel Build' },
      { key: 'P', action: 'Pause/Resume' },
      { key: 'ESC', action: 'Close/Cancel' },
    ];
    
    let yOffset = 35;
    hotkeys.forEach((hk) => {
      const keyText = new PIXI.Text(`${hk.key}: `, {
        fontFamily: 'Courier New',
        fontSize: 16,
        fill: 0xffd700,
        fontWeight: 'bold',
      });
      keyText.x = 10;
      keyText.y = yOffset;
      this.hotkeysPanel.addChild(keyText);
      
      const actionText = new PIXI.Text(hk.action, {
        fontFamily: 'Courier New',
        fontSize: 16,
        fill: 0xcccccc,
      });
      actionText.x = 10 + keyText.width; // Position right after the key text
      actionText.y = yOffset;
      this.hotkeysPanel.addChild(actionText);
      
      yOffset += 25;
    });
    
    this.uiContainer.addChild(this.hotkeysPanel);
  }

  private cancelBuildMode(): void {
    this.buildMode = null;
    this.buildMenu.selectType(null);
    // Only hide panel if no tower is selected
    if (!this.selectedTower) {
      this.towerInfoPanel.hide();
    }
    this.updateGhostPreview();
  }

  private updateMVPIndicators(): void {
    // Only show MVP indicators after wave 11
    const currentWave = this.gameState.getWaveNumber();
    if (currentWave < 11) {
      // Remove all indicators if we're before wave 11
      for (const [towerId, indicator] of this.mvpIndicators.entries()) {
        this.dynamicGraphics.removeChild(indicator);
        this.mvpIndicators.delete(towerId);
      }
      return;
    }

    // Get top 3 towers by damage
    const towerDamageArray = Array.from(this.towerDamageMap.entries())
      .map(([id, damage]) => ({ id, damage }))
      .sort((a, b) => b.damage - a.damage)
      .slice(0, 3);

    // Get all current towers
    const allTowers = this.towerSystem.getAllTowers();
    const currentTowerIds = new Set(allTowers.map(t => t.id));

    // Remove indicators for towers that no longer exist
    for (const [towerId, indicator] of this.mvpIndicators.entries()) {
      if (!currentTowerIds.has(towerId)) {
        this.dynamicGraphics.removeChild(indicator);
        this.mvpIndicators.delete(towerId);
        this.towerDamageMap.delete(towerId);
      }
    }

    // Update indicators - only show on #1 tower
    const mvpTowerId = towerDamageArray.length > 0 ? towerDamageArray[0].id : null;

    for (const tower of allTowers) {
      const isMVP = tower.id === mvpTowerId && towerDamageArray[0].damage > 0;

      if (isMVP) {
        // Create or update MVP indicator
        if (!this.mvpIndicators.has(tower.id)) {
          const indicator = this.createMVPIndicator();
          this.mvpIndicators.set(tower.id, indicator);
          this.dynamicGraphics.addChild(indicator);
          
          // Play shimmering sound when MVP indicator first appears
          this.playMVPShimmerSound();
        }
      } else {
        // Remove indicator if tower is no longer MVP
        const indicator = this.mvpIndicators.get(tower.id);
        if (indicator) {
          this.dynamicGraphics.removeChild(indicator);
          this.mvpIndicators.delete(tower.id);
        }
      }
    }
  }

  private playMVPShimmerSound(): void {
    if (!this.audioSystem) return;
    
    // Get audio context from AudioSystem
    const audioContext = this.audioSystem.getAudioContext();
    if (!audioContext) return;
    
    // Create ascending shimmering sound using Web Audio API
    const duration = 1.5; // 1.5 seconds
    const sampleRate = audioContext.sampleRate;
    const frameCount = sampleRate * duration;
    const buffer = audioContext.createBuffer(1, frameCount, sampleRate);
    const channelData = buffer.getChannelData(0);
    
    // Create ascending notes (multiple frequencies ascending)
    const startFreq = 440; // A4
    const endFreq = 880; // A5 (one octave higher)
    const numNotes = 5; // 5 ascending notes
    
    for (let i = 0; i < frameCount; i++) {
      const t = i / sampleRate;
      const progress = t / duration; // 0 to 1
      
      // Calculate current frequency (ascending)
      const currentFreq = startFreq + (endFreq - startFreq) * progress;
      
      // Create shimmering effect with multiple harmonics
      let sample = 0;
      for (let note = 0; note < numNotes; note++) {
        const noteFreq = currentFreq * (1 + note * 0.1); // Slight frequency variation
        const amplitude = 0.15 * Math.exp(-note * 0.3); // Decreasing amplitude for harmonics
        const phase = 2 * Math.PI * noteFreq * t;
        
        // Add envelope (fade in and out)
        const envelope = Math.sin(Math.PI * progress) * Math.exp(-progress * 0.5);
        
        sample += amplitude * Math.sin(phase) * envelope;
      }
      
      // Add shimmering effect (slight vibrato)
      const vibrato = 1 + 0.05 * Math.sin(2 * Math.PI * 5 * t); // 5 Hz vibrato
      sample *= vibrato;
      
      channelData[i] = sample;
    }
    
    // Play the sound
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    
    // Create gain node for volume control
    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0.4; // Volume level
    
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    source.start(0);
  }

  private createMVPIndicator(): PIXI.Container {
    const container = new PIXI.Container();
    
    // Golden crown icon
    const crown = new PIXI.Graphics();
    const size = 16;
    const centerX = 0;
    const centerY = -20; // Above the tower
    
    // Draw golden crown with shine effect
    crown.beginFill(0xffd700); // Gold
    // Crown base
    crown.drawRect(centerX - size / 2, centerY, size, size * 0.4);
    // Crown points (3 points)
    const pointHeight = size * 0.6;
    crown.moveTo(centerX - size / 2, centerY);
    crown.lineTo(centerX - size / 4, centerY - pointHeight);
    crown.lineTo(centerX, centerY - pointHeight * 0.7);
    crown.lineTo(centerX + size / 4, centerY - pointHeight);
    crown.lineTo(centerX + size / 2, centerY);
    crown.closePath();
    crown.endFill();
    
    // Shine effect (white highlight)
    crown.beginFill(0xffffff, 0.8);
    crown.drawRect(centerX - size / 4, centerY - pointHeight * 0.3, size / 2, size * 0.2);
    crown.endFill();
    
    // "MVP" text
    const mvpText = new PIXI.Text('MVP', {
      fontFamily: 'Courier New',
      fontSize: 10,
      fill: 0xffd700,
      fontWeight: 'bold',
      stroke: 0x000000,
      strokeThickness: 2,
    });
    mvpText.x = centerX - mvpText.width / 2;
    mvpText.y = centerY - pointHeight - 12;
    
    container.addChild(crown);
    container.addChild(mvpText);
    
    return container;
  }

  private updateMVPIndicatorPositions(): void {
    const tileSize = 32;
    for (const [towerId, indicator] of this.mvpIndicators.entries()) {
      const tower = this.towerSystem.getTower(towerId);
      if (tower) {
        const towerPixelPos = Vector2.fromTile(tower.position.x, tower.position.y, 32);
        indicator.x = towerPixelPos.x + tileSize / 2; // Center on tower
        indicator.y = towerPixelPos.y;
        
        // Animate shine effect (pulsing)
        const time = Date.now() / 1000;
        const pulse = 0.5 + Math.sin(time * 3) * 0.5; // Pulse between 0.5 and 1.0
        indicator.alpha = 0.7 + pulse * 0.3;
        indicator.scale.set(1.0 + Math.sin(time * 2) * 0.1); // Slight scale pulse
      }
    }
  }

  private togglePause(): void {
    // Only allow pause in single player mode
    if (this.gameState.getState().mode !== 'sp') {
      return;
    }

    this.isPaused = !this.isPaused;
    
    // Pause/resume music
    if (this.audioSystem) {
      if (this.isPaused) {
        this.audioSystem.pauseMusic();
        this.showPauseOverlay();
      } else {
        this.audioSystem.resumeMusic();
        this.hidePauseOverlay();
        this.logMessages.addMessage('Game Resumed');
      }
    }
  }

  private showPauseOverlay(): void {
    // Remove existing overlay if present
    if (this.pauseOverlay) {
      this.uiContainer.removeChild(this.pauseOverlay);
      this.pauseOverlay = null;
    }

    // Create pause overlay
    this.pauseOverlay = new PIXI.Container();
    
    // Grey out background
    const backdrop = new PIXI.Graphics();
    backdrop.beginFill(0x000000, 0.7);
    backdrop.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    backdrop.endFill();
    backdrop.eventMode = 'static';
    this.pauseOverlay.addChild(backdrop);

    // Popup window
    const popupWidth = 500;
    const popupHeight = 200;
    const popupX = (this.app.screen.width - popupWidth) / 2;
    const popupY = (this.app.screen.height - popupHeight) / 2;

    const popup = new PIXI.Graphics();
    popup.beginFill(0x222222, 0.95);
    popup.drawRoundedRect(0, 0, popupWidth, popupHeight, 12);
    popup.endFill();
    popup.lineStyle(3, 0x888888);
    popup.drawRoundedRect(0, 0, popupWidth, popupHeight, 12);
    popup.x = popupX;
    popup.y = popupY;
    popup.eventMode = 'static';
    this.pauseOverlay.addChild(popup);

    // Warning text
    const warningText = new PIXI.Text('GAME PAUSED', {
      fontFamily: 'Courier New',
      fontSize: 42,
      fill: 0xffff00,
      fontWeight: 'bold',
    });
    warningText.x = popupX + popupWidth / 2 - warningText.width / 2;
    warningText.y = popupY + 30;
    this.pauseOverlay.addChild(warningText);

    const messageText = new PIXI.Text('Press P to resume', {
      fontFamily: 'Courier New',
      fontSize: 23,
      fill: 0xffffff,
    });
    messageText.x = popupX + popupWidth / 2 - messageText.width / 2;
    messageText.y = popupY + 100;
    this.pauseOverlay.addChild(messageText);

    this.uiContainer.addChild(this.pauseOverlay);
  }

  private hidePauseOverlay(): void {
    if (this.pauseOverlay) {
      this.uiContainer.removeChild(this.pauseOverlay);
      this.pauseOverlay = null;
    }
  }

  private handleEscapeKey(): void {
    const settingsVisible = this.settingsMenu.getContainer().visible;
    if (settingsVisible) {
      this.hideSettings();
    } else {
      this.showSettings();
    }
    this.audioSystem?.playSound('button_click');
  }

  private renderMap(): void {
    const grid = this.gameState.getState().grid;
    if (!grid || grid.length === 0) {
      console.warn('Grid is empty, cannot render map');
      return;
    }

    const tileSize = 32;

    // Only redraw static map tiles if needed (when maze changes)
    if (this.needsMapRedraw) {
      this.mapGraphics.removeChildren();

      // Render tiles
      for (let y = 0; y < grid.length; y++) {
        if (!grid[y]) continue;
        
        for (let x = 0; x < grid[y].length; x++) {
          const tile = grid[y][x];
          if (!tile) continue;
          
          const graphics = new PIXI.Graphics();
          
          graphics.x = x * tileSize;
          graphics.y = y * tileSize;

          let color = 0x4a4a4a;
          switch (tile.type) {
            case 'empty':
              color = 0x4a4a4a; // Buildable - normal color
              break;
            case 'wall':
              color = 0x2a2a2a; // Non-buildable - darker
              break;
            case 'tower':
              color = 0x2a2a2a; // Non-buildable - darker (tower rendered separately)
              break;
            case 'spawn':
              color = 0x004400; // Non-buildable - darker green
              break;
            case 'goal':
              color = 0x440000; // Non-buildable - darker red
              break;
            case 'border':
              color = 0x0a0a0a; // Non-buildable - very dark
              break;
            default:
              color = 0x4a4a4a;
          }
          
          graphics.beginFill(color);
          graphics.drawRect(0, 0, tileSize, tileSize);
          graphics.endFill();

          this.mapGraphics.addChild(graphics);
        }
      }
      
      this.needsMapRedraw = false;
    }

    // Render grid overlay only when in build mode
    this.renderGrid();

    // Always redraw dynamic elements (towers and enemies)
    this.dynamicGraphics.removeChildren();

    // Release all pooled graphics from previous frame back to pool
    this.gpuRenderer.releaseAll();

    // Highlight tile that currently blocks the path
    if (!this.isPathValid && this.blockedHighlightTile) {
      const highlightGraphics = this.graphicsPool.acquire();
      highlightGraphics.x = this.blockedHighlightTile.x * tileSize;
      highlightGraphics.y = this.blockedHighlightTile.y * tileSize;
      highlightGraphics.beginFill(0xff0000, 0.4);
      highlightGraphics.drawRect(0, 0, tileSize, tileSize);
      highlightGraphics.endFill();
      this.dynamicGraphics.addChild(highlightGraphics);
    }

    // Render towers using pooled graphics
    for (const tower of this.towerSystem.getAllTowers()) {
      const graphics = this.graphicsPool.acquire();
      // Tower position is stored in tile coordinates, convert to pixel
      const towerPixelPos = Vector2.fromTile(tower.position.x, tower.position.y, 32);
      graphics.x = towerPixelPos.x;
      graphics.y = towerPixelPos.y;
      
      // Different colors for different tower types
      let color = 0x87ceeb; // Light blue for arrow (default)
      if (tower.type === 'arrow') color = 0x87ceeb; // Light blue
      else if (tower.type === 'cannon') color = 0x800080; // Purple
      else if (tower.type === 'sniper') color = 0xffffff; // White
      else if (tower.type === 'fire') color = 0xff4500;
      else if (tower.type === 'ice') color = 0x00ffff;
      else if (tower.type === 'lightning') color = 0xffff00;
      
      // Draw triangle instead of circle
      const centerX = tileSize / 2;
      const centerY = tileSize / 2;
      const size = 12;
      graphics.beginFill(color);
      graphics.moveTo(centerX, centerY - size);
      graphics.lineTo(centerX - size, centerY + size);
      graphics.lineTo(centerX + size, centerY + size);
      graphics.lineTo(centerX, centerY - size);
      graphics.endFill();
      
      // Show upgrade level - draw triangle outline
      if (tower.hasUpgradeA || tower.hasUpgradeB) {
        graphics.lineStyle(2, 0xffffff);
        const upgradeSize = 14;
        graphics.moveTo(centerX, centerY - upgradeSize);
        graphics.lineTo(centerX - upgradeSize, centerY + upgradeSize);
        graphics.lineTo(centerX + upgradeSize, centerY + upgradeSize);
        graphics.lineTo(centerX, centerY - upgradeSize);
      }
      
      // Highlight grey tile background if tower has upgrade B
      if (tower.hasUpgradeB) {
        graphics.beginFill(0xcccccc, 0.3); // Light grey with transparency
        graphics.drawRect(0, 0, tileSize, tileSize);
        graphics.endFill();
      }
      
      // Visual feedback for arrow tower B ability (Rapid Fire)
      if (tower.type === 'arrow' && tower.hasUpgradeB && tower.lastAbilityTime !== undefined && tower.lastAbilityTime >= 0) {
        const currentTime = this.lastTime;
        const timeSinceLastAbility = currentTime - tower.lastAbilityTime;
        
        if (tower.abilityActive) {
          // ACTIVE: Pulsing glowing ring
          const pulseIntensity = 0.5 + Math.sin(Date.now() / 100) * 0.5; // Pulsing effect (0.0 to 1.0)
          graphics.lineStyle(3, 0xffff00, pulseIntensity); // Yellow glow with pulsing alpha
          graphics.drawCircle(centerX, centerY, 20);
          graphics.lineStyle(0); // Reset line style
          
          // Rotating energy lines (4 lines spinning around tower)
          const rotation = (Date.now() / 500) % (Math.PI * 2); // Full rotation every 0.5 seconds
          graphics.lineStyle(2, 0xffffff, 0.7); // White energy lines
          for (let i = 0; i < 4; i++) {
            const angle = rotation + (i * Math.PI / 2);
            const x1 = centerX + Math.cos(angle) * 10;
            const y1 = centerY + Math.sin(angle) * 10;
            const x2 = centerX + Math.cos(angle) * 18;
            const y2 = centerY + Math.sin(angle) * 18;
            graphics.moveTo(x1, y1);
            graphics.lineTo(x2, y2);
          }
          graphics.lineStyle(0); // Reset line style
        } else if (timeSinceLastAbility < 14) {
          // COOLDOWN: Draw cooldown indicator (circular progress bar)
          const cooldownProgress = timeSinceLastAbility / 14; // 0.0 to 1.0
          const cooldownAngle = cooldownProgress * Math.PI * 2; // 0 to 2π
          
          // Background circle (grey)
          graphics.lineStyle(2, 0x666666, 0.3);
          graphics.drawCircle(centerX, centerY, 20);
          
          // Cooldown progress arc (yellow, fills clockwise)
          graphics.lineStyle(3, 0xffaa00, 0.6);
          graphics.arc(centerX, centerY, 20, -Math.PI / 2, -Math.PI / 2 + cooldownAngle);
          graphics.lineStyle(0); // Reset line style
        }
      }
      
      // Show range indicator if selected
      if (this.selectedTower && this.selectedTower.id === tower.id) {
        // Blue highlight for selected tower
        graphics.lineStyle(3, 0x0088ff, 1.0);
        graphics.drawRect(0, 0, tileSize, tileSize);
        // Range indicator in yellow
        graphics.lineStyle(2, 0xffff00, 1.0);
        graphics.drawCircle(tileSize / 2, tileSize / 2, tower.range * tileSize);
      }
      
      this.dynamicGraphics.addChild(graphics);
      
      // MVP indicator is rendered separately in updateMVPIndicatorPositions
      
      // Show HP bar if tower is damaged
      if (tower.hp < tower.maxHp) {
        const hpBarGraphics = this.graphicsPool.acquire();
        const barWidth = tileSize;
        const barHeight = 4;
        hpBarGraphics.x = towerPixelPos.x;
        hpBarGraphics.y = towerPixelPos.y + (tileSize / 2) - (barHeight / 2); // Position in middle of tile
        
        const hpPercent = Math.max(0, Math.min(1, tower.hp / tower.maxHp));
        
        // Background bar (dark red/black)
        hpBarGraphics.beginFill(0x330000);
        hpBarGraphics.drawRect(0, 0, barWidth, barHeight);
        hpBarGraphics.endFill();
        
        // HP fill bar (color based on HP percentage)
        const hpColor = hpPercent > 0.6 ? 0x00ff00 : hpPercent > 0.3 ? 0xffff00 : 0xff0000;
        hpBarGraphics.beginFill(hpColor);
        hpBarGraphics.drawRect(0, 0, barWidth * hpPercent, barHeight);
        hpBarGraphics.endFill();
        
        this.dynamicGraphics.addChild(hpBarGraphics);
      }
    }

    // Render walls with selection highlight using pooled graphics
    const state = this.gameState.getState();
    for (const wall of state.walls) {
      const wallPixelPos = Vector2.fromTile(wall.position.x, wall.position.y, 32);
      const graphics = this.graphicsPool.acquire();
      graphics.x = wallPixelPos.x;
      graphics.y = wallPixelPos.y;
      
      // Draw wall
      graphics.beginFill(0x888888);
      graphics.drawRect(0, 0, tileSize, tileSize);
      graphics.endFill();
      
      // Blue highlight if selected
      if (this.selectedWall && this.selectedWall.id === wall.id) {
        graphics.lineStyle(3, 0x0088ff, 1.0);
        graphics.drawRect(0, 0, tileSize, tileSize);
      }
      
      this.dynamicGraphics.addChild(graphics);
      
      // Show HP bar if wall is damaged
      if (wall.hp < wall.maxHp) {
        const hpBarGraphics = this.graphicsPool.acquire();
        const barWidth = tileSize;
        const barHeight = 4;
        hpBarGraphics.x = wallPixelPos.x;
        hpBarGraphics.y = wallPixelPos.y + (tileSize / 2) - (barHeight / 2); // Position in middle of tile
        
        const hpPercent = Math.max(0, Math.min(1, wall.hp / wall.maxHp));
        
        // Background bar (dark red/black)
        hpBarGraphics.beginFill(0x330000);
        hpBarGraphics.drawRect(0, 0, barWidth, barHeight);
        hpBarGraphics.endFill();
        
        // HP fill bar (color based on HP percentage)
        const hpColor = hpPercent > 0.6 ? 0x00ff00 : hpPercent > 0.3 ? 0xffff00 : 0xff0000;
        hpBarGraphics.beginFill(hpColor);
        hpBarGraphics.drawRect(0, 0, barWidth * hpPercent, barHeight);
        hpBarGraphics.endFill();
        
        this.dynamicGraphics.addChild(hpBarGraphics);
      }
    }

    // Render enemies using GPU-accelerated rendering
    // Note: HP bars and status effects are rendered separately using pooled graphics
    const enemies = this.enemySystem.getAllEnemies();
    
    // GPU render enemy bodies (main circles) - VERY FAST
    this.gpuRenderer.renderEnemies(enemies);
    
    // Render HP bars and status effects using pooled graphics (overlay on top of GPU sprites)
    for (const enemy of enemies) {
      // Check for armor reduction debuff - add visual indicator
      const hasArmorReduction = enemy.statusEffects.some(effect => effect.type === 'armor_reduction');
      if (hasArmorReduction) {
        const graphics = this.graphicsPool.acquire();
      graphics.x = enemy.position.x;
      graphics.y = enemy.position.y;
      
        // Draw broken shield icon (purple outline around enemy + purple arc below)
        graphics.lineStyle(2, 0x9b59b6, 0.8); // Purple outline
        graphics.drawCircle(0, 0, 10);
        graphics.lineStyle(0); // Reset line style
        
        // Draw small shield symbol with crack
        graphics.beginFill(0x9b59b6, 0.7); // Purple shield
        graphics.moveTo(-4, 10);
        graphics.lineTo(0, 14);
        graphics.lineTo(4, 10);
        graphics.lineTo(4, 6);
        graphics.lineTo(-4, 6);
        graphics.lineTo(-4, 10);
      graphics.endFill();
        
        // Draw crack in shield (red line)
        graphics.lineStyle(1, 0xff0000, 1.0);
        graphics.moveTo(-2, 6);
        graphics.lineTo(0, 10);
        graphics.lineTo(2, 14);
        graphics.lineStyle(0);
        
        this.dynamicGraphics.addChild(graphics);
      }
      
      // Show HP bar for all enemies (always visible, updates correctly)
      const hpPercent = Math.max(0, Math.min(1, enemy.hp / enemy.maxHp)); // Clamp between 0 and 1
      const graphics = this.graphicsPool.acquire();
      graphics.x = enemy.position.x;
      graphics.y = enemy.position.y;
      
      // Background bar (black)
      graphics.beginFill(0x000000);
      graphics.drawRect(-10, -12, 20, 3);
      graphics.endFill();
      // HP fill bar (green if >50%, red if <=50%)
      const hpColor = hpPercent > 0.5 ? 0x00ff00 : hpPercent > 0.25 ? 0xffff00 : 0xff0000;
      graphics.beginFill(hpColor);
      graphics.drawRect(-10, -12, 20 * hpPercent, 3);
      graphics.endFill();
      
      this.dynamicGraphics.addChild(graphics);
    }

    // Always render path (green line when valid, red when blocked - shows last valid path)
    const pathToRender = this.isPathValid ? this.globalPath : this.lastValidPath;
    if (pathToRender.length > 1) {
      const pathGraphics = this.graphicsPool.acquire();
      // Green line when path is valid, red when blocked (showing last valid path)
      const pathColor = this.isPathValid ? 0x00ff00 : 0xff0000;
      pathGraphics.lineStyle(2, pathColor, 0.5); // Semi-transparent
      
      // Draw lines between all waypoints
      for (let i = 0; i < pathToRender.length - 1; i++) {
        const from = pathToRender[i];
        const to = pathToRender[i + 1];
        if (i === 0) {
          pathGraphics.moveTo(from.x, from.y);
        }
        pathGraphics.lineTo(to.x, to.y);
      }
      
      this.dynamicGraphics.addChild(pathGraphics);
    }

    // Render purple path for flying/ghost units (straight line from spawn to goal)
    const hasFlyingOrGhost = this.enemySystem.getAllEnemies().some(e => e.type === 'flying' || e.type === 'ghost');
    if (hasFlyingOrGhost) {
      const state = this.gameState.getState();
      if (state.spawns.length > 0 && state.goals.length > 0) {
        const spawn = Vector2.fromTile(state.spawns[0].x, state.spawns[0].y, 32).add(new Vector2(16, 16)); // Center of tile
        const goal = Vector2.fromTile(state.goals[0].x, state.goals[0].y, 32).add(new Vector2(16, 16));
        
        const purplePathGraphics = this.graphicsPool.acquire();
        purplePathGraphics.lineStyle(3, 0x9b59b6, 0.4); // Purple with transparency
        purplePathGraphics.moveTo(spawn.x, spawn.y);
        purplePathGraphics.lineTo(goal.x, goal.y);
        
        this.dynamicGraphics.addChild(purplePathGraphics);
      }
    }

    // Render AoE effects (expanding/fading circles for cannon impacts) using pooled graphics
    for (const effect of this.aoeEffects) {
      const aoeGraphics = this.graphicsPool.acquire();
      aoeGraphics.x = effect.position.x;
      aoeGraphics.y = effect.position.y;
      
      // Calculate fade based on remaining duration
      const fadeProgress = effect.duration / effect.maxDuration;
      const alpha = fadeProgress * 0.5; // Max 0.5 opacity, fades to 0
      
      // Draw filled circle
      aoeGraphics.beginFill(effect.color, alpha * 0.3); // Semi-transparent fill
      aoeGraphics.drawCircle(0, 0, effect.radius);
      aoeGraphics.endFill();
      
      // Draw circle outline
      aoeGraphics.lineStyle(3, effect.color, alpha);
      aoeGraphics.drawCircle(0, 0, effect.radius);
      
      this.dynamicGraphics.addChild(aoeGraphics);
    }

    // Render projectiles using GPU-accelerated rendering - VERY FAST
    this.gpuRenderer.renderProjectiles(this.projectiles);
  }
  
  private createAoeEffect(position: Vector2, color: number, aoeRadiusInTiles: number): void {
    // Convert tile radius to pixel radius, then make visual 25% smaller than actual damage AoE
    const damageRadius = aoeRadiusInTiles * 32; // Actual damage radius
    const maxRadius = damageRadius * 0.75; // Visual effect is 25% smaller
    
    console.log(`[AOE VISUAL] Creating AoE effect: aoeRadiusInTiles=${aoeRadiusInTiles}, damageRadius=${damageRadius}px, visualRadius=${maxRadius}px (${(maxRadius/32).toFixed(2)} tiles)`);
    
    this.aoeEffects.push({
      position: position.clone(),
      radius: 0, // Start at 0, will expand
      maxRadius: maxRadius,
      duration: 0.4, // Effect lasts 0.4 seconds
      maxDuration: 0.4,
      color: color,
    });
  }

  private renderGrid(): void {
    // Clear grid first
    this.gridGraphics.clear();
    
    // Only show grid when in build mode
    if (!this.buildMode) return;

    const grid = this.gameState.getState().grid;
    if (!grid || grid.length === 0) return;

    const tileSize = 32;
    // For the new 20x20 map, grid is from (0,0) to (20,20) with no borders
    const gridSize = grid.length; // Should be 20 for single-player
    const gridWidth = grid[0]?.length || gridSize; // Should be 20 for single-player
    
    // Calculate grid bounds in pixels (0 to gridSize * tileSize)
    const gridStartX = 0;
    const gridStartY = 0;
    const gridEndX = gridWidth * tileSize;
    const gridEndY = gridSize * tileSize;

    this.gridGraphics.lineStyle(1, 0x888888, 0.6); // Brighter gray lines

    // Draw vertical lines for the entire grid
    for (let x = 0; x <= gridWidth; x++) {
      this.gridGraphics.moveTo(x * tileSize, gridStartY);
      this.gridGraphics.lineTo(x * tileSize, gridEndY);
    }

    // Draw horizontal lines for the entire grid
    for (let y = 0; y <= gridSize; y++) {
      this.gridGraphics.moveTo(gridStartX, y * tileSize);
      this.gridGraphics.lineTo(gridEndX, y * tileSize);
    }
  }

  private updateGhostPreview(): void {
    // Remove existing ghost preview
    if (this.ghostPreview) {
      this.previewGraphics.removeChild(this.ghostPreview);
      this.ghostPreview = null;
    }

    // Only show preview if in build mode
    if (!this.buildMode) return;

    const tileSize = 32;
    // Convert screen coordinates to game container coordinates
    // Game container is positioned at (640, 220) to center the map
    const containerX = this.mousePosition.x - 640; // Convert to game container space
    const containerY = this.mousePosition.y - 220; // Convert to game container space
    // Account for camera pan offset (camera pan is within game container)
    const worldX = containerX - this.cameraX;
    const worldY = containerY - this.cameraY;
    const tileX = Math.floor(worldX / tileSize);
    const tileY = Math.floor(worldY / tileSize);
    const pixelX = tileX * tileSize;
    const pixelY = tileY * tileSize;

    const state = this.gameState.getState();
    
    // Check if any enemy is on this tile
    let enemyOnTile = false;
    const enemies = this.enemySystem.getAllEnemies();
    for (const enemy of enemies) {
      const enemyTile = enemy.position.toTile(32);
      if (enemyTile.x === tileX && enemyTile.y === tileY) {
        enemyOnTile = true;
        break;
      }
    }
    
    const isValidPlacement = this.mazeBuilder.validatePlacement(
      tileX,
      tileY,
      this.buildMode === 'wall' ? 'wall' : 'tower',
      state.grid,
      state.spawns,
      state.goals
    );

    // Check if player can afford it
    let canAfford = false;
    if (this.buildMode === 'wall') {
      canAfford = this.economySystem.canAfford(GAME_CONSTANTS.WALL_COST);
      // Update wall info panel affordability
      if (this.towerInfoPanel.getContainer().visible) {
        this.towerInfoPanel.showWall(canAfford);
      }
    } else {
      canAfford = this.economySystem.canAfford(TOWER_STATS[this.buildMode].cost);
    }

    const isValid = isValidPlacement && canAfford && !enemyOnTile; // Red if enemy on tile

    this.ghostPreview = new PIXI.Graphics();
    
    if (this.buildMode === 'wall') {
      // Draw wall preview
      const color = isValid ? 0x00ff00 : 0xff0000;
      this.ghostPreview.beginFill(color, 0.4);
      this.ghostPreview.drawRect(pixelX, pixelY, tileSize, tileSize);
      this.ghostPreview.endFill();
      this.ghostPreview.lineStyle(2, color, 0.6);
      this.ghostPreview.drawRect(pixelX, pixelY, tileSize, tileSize);
    } else {
      // Draw tower preview - triangle
      const color = isValid ? 0x00ff00 : 0xff0000;
      let towerColor = 0x87ceeb; // Light blue for arrow (default)
      if (this.buildMode === 'arrow') towerColor = 0x87ceeb; // Light blue
      else if (this.buildMode === 'cannon') towerColor = 0x800080; // Purple
      else if (this.buildMode === 'sniper') towerColor = 0xffffff; // White
      
      const centerX = pixelX + tileSize / 2;
      const centerY = pixelY + tileSize / 2;
      const size = 12;
      this.ghostPreview.beginFill(towerColor, 0.4);
      this.ghostPreview.moveTo(centerX, centerY - size);
      this.ghostPreview.lineTo(centerX - size, centerY + size);
      this.ghostPreview.lineTo(centerX + size, centerY + size);
      this.ghostPreview.lineTo(centerX, centerY - size);
      this.ghostPreview.endFill();
      this.ghostPreview.lineStyle(2, color, 0.6);
      this.ghostPreview.moveTo(centerX, centerY - size);
      this.ghostPreview.lineTo(centerX - size, centerY + size);
      this.ghostPreview.lineTo(centerX + size, centerY + size);
      this.ghostPreview.lineTo(centerX, centerY - size);
      
      // Draw max range indicator for towers
      const towerRange = TOWER_STATS[this.buildMode].range;
      const maxRange = towerRange * tileSize;
      this.ghostPreview.lineStyle(2, 0xffff00, 0.5); // Yellow, semi-transparent
      this.ghostPreview.drawCircle(centerX, centerY, maxRange);
    }

    this.previewGraphics.addChild(this.ghostPreview);
  }

  sellTower(tower: Tower): void {
    const state = this.gameState.getState();
    // Tower position is already in tile coordinates
    const tileX = Math.floor(tower.position.x);
    const tileY = Math.floor(tower.position.y);
    
    // Remove from blocking buildings list if it was there
    const buildingTile = new Vector2(tileX, tileY);
    this.removeBlockingBuilding(buildingTile);
    
    // Calculate sell value (50% of total gold spent, including upgrades)
    const baseCost = TOWER_STATS[tower.type].cost;
    let totalCost = baseCost;
    
    // Add upgrade costs if applicable (cumulative)
    if (tower.hasUpgradeA) {
      totalCost += UPGRADE_A_STATS[tower.type].cost;
    }
    if (tower.hasUpgradeB) {
      totalCost += UPGRADE_B_STATS[tower.type].cost;
    }
    if (tower.hasUpgradeC && (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper')) {
      totalCost += UPGRADE_C_STATS[tower.type].cost;
    }
    
    // Calculate base sell value (50% of total cost)
    let sellValue = Math.floor(totalCost * 0.5);
    
    // Apply 60% penalty if building is damaged (< 100% HP)
    if (tower.hp < tower.maxHp) {
      sellValue = Math.floor(sellValue * 0.4); // 60% reduction = 40% of original sell value
    }
    // Play both gold sounds (random order)
    if (this.audioSystem) {
      if (Math.random() < 0.5) {
        // Play gold_gain_1 then gold_gain_2
        this.audioSystem.playSound('gold_gain_1');
        setTimeout(() => {
          this.audioSystem?.playSound('gold_gain_2');
        }, 150);
      } else {
        // Play gold_gain_2 then gold_gain_1
        this.audioSystem.playSound('gold_gain_2');
        setTimeout(() => {
          this.audioSystem?.playSound('gold_gain_1');
        }, 150);
      }
    }
    
    // Remove tower from system
    this.towerSystem.removeTower(tower.id);
    
    // Remove MVP indicator if present
    const mvpIndicator = this.mvpIndicators.get(tower.id);
    if (mvpIndicator) {
      this.dynamicGraphics.removeChild(mvpIndicator);
      this.mvpIndicators.delete(tower.id);
    }
    this.towerDamageMap.delete(tower.id);
    
    // Remove from state
    const towerIndex = state.towers.findIndex(t => t.id === tower.id);
    if (towerIndex >= 0) {
      state.towers.splice(towerIndex, 1);
    }
    
    // Clear the tile
    if (tileY >= 0 && tileY < state.grid.length && 
        tileX >= 0 && tileX < state.grid[tileY].length) {
      state.grid[tileY][tileX].type = 'empty';
    }
    
    // Refund gold
    this.economySystem.addGold(sellValue);
    this.logMessages.addMessage(`+${sellValue}g (sold ${tower.type} tower)`);
    
    // Clear selection
    this.selectedTower = null;
    this.towerInfoPanel.hide();
    
    // Update path
    this.pathfinder.clearCache();
    this.updateGlobalPath();
    
    // Exit block breaker mode for enemies targeting this building
    this.exitBlockBreakerForBuilding(buildingTile);
    
    this.forceEnemyRepath();
    
    this.needsMapRedraw = true;
    this.renderMap();
  }

  sellWall(wall: Wall): void {
    const state = this.gameState.getState();
    // Wall position is already in tile coordinates
    const tileX = Math.floor(wall.position.x);
    const tileY = Math.floor(wall.position.y);
    
    // Remove from blocking buildings list if it was there
    const buildingTile = new Vector2(tileX, tileY);
    this.removeBlockingBuilding(buildingTile);
    
    // Calculate base sell value (50% of base cost)
    let sellValue = Math.floor(GAME_CONSTANTS.WALL_COST * 0.5);
    
    // Apply 60% penalty if building is damaged (< 100% HP)
    if (wall.hp < wall.maxHp) {
      sellValue = Math.floor(sellValue * 0.4); // 60% reduction = 40% of original sell value
    }
    // Play both gold sounds (random order)
    if (this.audioSystem) {
      if (Math.random() < 0.5) {
        // Play gold_gain_1 then gold_gain_2
        this.audioSystem.playSound('gold_gain_1');
        setTimeout(() => {
          this.audioSystem?.playSound('gold_gain_2');
        }, 150);
      } else {
        // Play gold_gain_2 then gold_gain_1
        this.audioSystem.playSound('gold_gain_2');
        setTimeout(() => {
          this.audioSystem?.playSound('gold_gain_1');
        }, 150);
      }
    }
    
    // Remove from state
    const wallIndex = state.walls.findIndex(w => w.id === wall.id);
    if (wallIndex >= 0) {
      state.walls.splice(wallIndex, 1);
    }
    
    // Clear the tile
    if (tileY >= 0 && tileY < state.grid.length && 
        tileX >= 0 && tileX < state.grid[tileY].length) {
      state.grid[tileY][tileX].type = 'empty';
    }
    
    // Refund gold
    this.economySystem.addGold(sellValue);
    this.logMessages.addMessage(`+${sellValue}g (sold wall)`);
    
    // Clear selection
    this.selectedWall = null;
    this.towerInfoPanel.hide();
    
    // Update path
    this.pathfinder.clearCache();
    this.updateGlobalPath();
    
    // Exit block breaker mode for enemies targeting this building
    this.exitBlockBreakerForBuilding(buildingTile);
    
    this.forceEnemyRepath();
    
    this.needsMapRedraw = true;
    this.renderMap();
  }

  private update(deltaTime: number): void {
    // Skip all updates if paused
    if (this.isPaused) {
      // Still update UI to show pause state
      this.hud.update(this.gameState.getState(), this.gameTimer, this.totalKills, this.totalDamage);
      this.hud.setWaveActive(this.isWaveActive);
      return;
    }

    this.lastTime += deltaTime;

    // Update economy (GPM)
    this.economySystem.update(deltaTime);
    this.gameState.setEconomy(this.economySystem.getState());
    
    // Update repair system
    this.repairSystem.update(deltaTime, this.towerSystem, this.gameState.getState().walls);
    
    // Update damage numbers
    this.damageNumbers.update(deltaTime);
    
    // Update game timer (only when wave is active)
    if (this.isWaveActive) {
      this.gameTimer += deltaTime;
    }
    
    // Update UI
    this.hud.update(this.gameState.getState(), this.gameTimer, this.totalKills, this.totalDamage);
    this.hud.setWaveActive(this.isWaveActive);
    
    // Update tower info panel if tower is selected - preserve selection by updating reference
    if (this.selectedTower) {
      const tower = this.towerSystem.getTower(this.selectedTower.id);
      if (tower) {
        // Update selectedTower reference to keep it in sync (tower object might have changed)
        this.selectedTower = tower;
        const canAffordA = this.economySystem.canAfford(UPGRADE_A_STATS[tower.type].cost);
        const canAffordB = this.economySystem.canAfford(UPGRADE_B_STATS[tower.type].cost);
        const canAffordC = (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper')
          ? this.economySystem.canAfford(UPGRADE_C_STATS[tower.type].cost)
          : false;
        this.towerInfoPanel.refresh(tower, canAffordA, canAffordB, canAffordC, false);
      } else {
        // Tower was destroyed
        this.selectedTower = null;
        this.towerInfoPanel.hide();
      }
    }
    
    // Update wall info panel if wall is selected - preserve selection by updating reference
    if (this.selectedWall) {
      const state = this.gameState.getState();
      const wall = state.walls.find(w => w.id === this.selectedWall!.id);
      if (wall) {
        // Update selectedWall reference to keep it in sync
        this.selectedWall = wall;
      } else {
        // Wall was destroyed
        this.selectedWall = null;
        this.towerInfoPanel.hide();
      }
    }
    
    // Update wall info panel if in wall build mode
    if (this.buildMode === 'wall' && this.towerInfoPanel.getContainer().visible) {
      const canAfford = this.economySystem.canAfford(GAME_CONSTANTS.WALL_COST);
      this.towerInfoPanel.showWall(canAfford);
    }

    // Update enemies
    const state = this.gameState.getState();
    const goal = state.goals[0];
    
    const enemiesToRemove: string[] = [];
    
    // Track if any enemy needs repath (in contact with building)
    let needsRepath = false;
    
    for (const enemy of this.enemySystem.getAllEnemies()) {
      // Initialize block breaker properties if not set
      if (enemy.isBlockBreaker === undefined) enemy.isBlockBreaker = false;
      if (enemy.blockBreakerTarget === undefined) enemy.blockBreakerTarget = null;
      if (enemy.attackCooldown === undefined) enemy.attackCooldown = 0;
      if (enemy.blockingBuildingTile === undefined) enemy.blockingBuildingTile = null;
      if (enemy.greenBlockTime === undefined) enemy.greenBlockTime = 0;
      if (enemy.greenRerouteCount === undefined) enemy.greenRerouteCount = 0;
      if (enemy.greenRerouteCooldown === undefined) enemy.greenRerouteCooldown = 0;
      
      // FIRST: Check if enemy reached goal - do this BEFORE path assignment to prevent reassignment
      // Also check if enemy is at the last waypoint of their path (indicates they reached goal)
      const hasReachedGoal = this.enemySystem.hasReachedGoal(enemy, goal);
      const isAtLastWaypoint = enemy.path.length > 0 && 
                               enemy.currentPathIndex >= enemy.path.length - 1 &&
                               enemy.position.distance(enemy.path[enemy.path.length - 1]) < 32; // Within 1 tile
      
      if (hasReachedGoal || isAtLastWaypoint) {
        console.log(`[GOAL] Enemy ${enemy.id} reached goal at (${enemy.position.x.toFixed(1)}, ${enemy.position.y.toFixed(1)}), hasReachedGoal=${hasReachedGoal}, isAtLastWaypoint=${isAtLastWaypoint}, path.length=${enemy.path.length}, currentIndex=${enemy.currentPathIndex}`);
        this.gameState.takeDamage(enemy.goalDamage);
        
        // Limit death sounds to max 3 simultaneous
        if (this.deathSoundCount < 3) {
          this.deathSoundCount++;
          this.audioSystem?.playSound('enemy_reach_goal');
          // Decrement count after sound duration (approximately 0.5 seconds)
          setTimeout(() => {
            if (this.deathSoundCount > 0) {
              this.deathSoundCount--;
            }
          }, 500);
        }
        
        enemiesToRemove.push(enemy.id);
        
        // Check for game over
        if (this.gameState.isDead()) {
          console.log('Game Over!');
          // Could show game over screen here
        }
        continue; // Skip path assignment and other logic for this enemy
      }

      // Check if enemy is dead - also do this early
      if (enemy.hp <= 0) {
        const goldAwarded = this.economySystem.awardKillGold(enemy, this.gameState.getWaveNumber());
        const enemyTypeName = enemy.type.charAt(0).toUpperCase() + enemy.type.slice(1);
        this.logMessages.addMessage(`+${goldAwarded}g (${enemyTypeName} killed)`);
        this.totalKills++;
        this.audioSystem?.playSound('enemy_death');
        this.audioSystem?.playSound('gold_gain');
        
        // Handle splitter splitting BEFORE removing
        if (enemy.type === 'splitter') {
          console.log(`[SPLITTER] Enemy ${enemy.id} is a splitter and died, splitting into 2 smaller enemies`);
          
          // Create two smaller splitters
          const currentTime = Date.now() / 1000;
          for (let i = 0; i < 2; i++) {
            const angle = (Math.PI * 2 * i) / 2 + Math.random() * 0.5;
            const distance = 48 + Math.random() * 32;
            const offsetX = Math.cos(angle) * distance;
            const offsetY = Math.sin(angle) * distance;
            const splitPosition = new Vector2(
              enemy.position.x + offsetX,
              enemy.position.y + offsetY
            );
            
            const smallSplitter: Enemy = {
              id: `splitter_small_${Date.now()}_${Math.random()}_${i}`,
              type: 'ground',
              position: splitPosition,
              hp: Math.floor(enemy.maxHp * 0.3),
              maxHp: Math.floor(enemy.maxHp * 0.3),
              speed: enemy.baseSpeed * 1.5,
              baseSpeed: enemy.baseSpeed * 1.5,
              path: enemy.path.map(p => p.clone()),
              currentPathIndex: enemy.currentPathIndex,
              statusEffects: [],
              isElite: false,
              goalDamage: 1,
              isPhased: false,
              idleTime: 0,
              lastPosition: splitPosition.clone(),
              immuneUntil: currentTime + 1.0,
              waveLevel: enemy.waveLevel,
            };
            
            this.enemySystem.getAllEnemies().push(smallSplitter);
            console.log(`[SPLITTER] Created small splitter ${smallSplitter.id} at (${splitPosition.x.toFixed(1)}, ${splitPosition.y.toFixed(1)}) with ${smallSplitter.hp} HP`);
          }
          
          console.log(`[SPLITTER] Total enemies after split: ${this.enemySystem.getAllEnemies().length}`);
        }
        
        enemiesToRemove.push(enemy.id);
        continue; // Skip path assignment and other logic for this enemy
      }
      
      // Check if this enemy needs repath (in contact with building)
      if ((enemy as any)._needsRepath) {
        needsRepath = true;
        (enemy as any)._needsRepath = false; // Clear flag
      }

      // FIRST: Check if enemy is in block breaker mode OR on a building - skip path assignment
      if (enemy.isBlockBreaker && enemy.blockBreakerTarget) {
        // Enemy is in block breaker mode - ensure they stay at target and don't get paths assigned
        const enemyTile = enemy.position.toTile(32);
        const isAtTarget = enemyTile.x === enemy.blockBreakerTarget.x && 
                          enemyTile.y === enemy.blockBreakerTarget.y;
        if (isAtTarget) {
          // Enemy is at target - keep path cleared
          enemy.path = [];
          enemy.currentPathIndex = 0;
        }
        // Don't continue - enemies need to reach updateEnemy to attack
      } else
      
      // SIMPLIFIED BLOCK BREAKER LOGIC:
      // - Only engage siege mode while the global path is red
      // - When the path is green, clear any lingering blockingBuildingTile markers
      if (enemy.blockingBuildingTile && enemy.type !== 'ghost' && enemy.type !== 'flying') {
        const enemyTile = enemy.position.toTile(32);
        const buildingTile = enemy.blockingBuildingTile;
        const distanceToBuilding = Math.abs(enemyTile.x - buildingTile.x) + Math.abs(enemyTile.y - buildingTile.y);
        
        // Check if building still exists
        const isBuildingStillThere = buildingTile.y >= 0 && buildingTile.y < state.grid.length &&
                                      buildingTile.x >= 0 && buildingTile.x < state.grid[buildingTile.y].length &&
                                      (state.grid[buildingTile.y][buildingTile.x].type === 'wall' || 
                                       state.grid[buildingTile.y][buildingTile.x].type === 'tower');
        
        if (!isBuildingStillThere) {
          // Building removed - clear markers
          enemy.blockingBuildingTile = null;
          if (enemy.isBlockBreaker) {
            enemy.isBlockBreaker = false;
            enemy.blockBreakerTarget = null;
            enemy.attackCooldown = 0;
            enemy.speed = enemy.baseSpeed;
          }
          enemy.greenBlockTime = 0;
          enemy.greenRerouteCount = 0;
          enemy.greenRerouteCooldown = 0;
          continue;
        }
        
        if (this.isPathValid) {
          // Path is green: attempt pathfinding first
          enemy.greenBlockTime = (enemy.greenBlockTime || 0) + deltaTime;
          
          let repathSucceeded = false;
          
          if (enemy.greenRerouteCooldown && enemy.greenRerouteCooldown > 0) {
            enemy.greenRerouteCooldown = Math.max(0, enemy.greenRerouteCooldown - deltaTime);
          } else if ((enemy.greenRerouteCount || 0) < 3) {
            if (this.globalPath.length > 0) {
              // Try closest waypoints first
              const sortedIndices = this.globalPath
                .map((point, index) => ({ index, dist: enemy.position.distance(point) }))
                .sort((a, b) => a.dist - b.dist);
              
            for (let attempt = 0; attempt < Math.min(sortedIndices.length, 5); attempt++) {
                const waypointIndex = sortedIndices[attempt].index;
                const waypoint = this.globalPath[waypointIndex];
                const waypointTile = waypoint.toTile(32);
                
                const pathToWaypoint = this.pathfinder.findPath(
                  new Vector2(enemyTile.x, enemyTile.y),
                  new Vector2(waypointTile.x, waypointTile.y),
                  state.grid
                );
                
                if (pathToWaypoint.length > 0) {
                const touchesBlockedTile = pathToWaypoint.some(tilePos => 
                  tilePos.x === buildingTile.x && tilePos.y === buildingTile.y
                );
                if (touchesBlockedTile) {
                  continue;
                }
                  const toWaypointPixels = pathToWaypoint.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
                  const remainderOfGlobal = this.globalPath.slice(waypointIndex).map(p => p.clone());
                  enemy.path = [...toWaypointPixels, ...remainderOfGlobal];
                  enemy.currentPathIndex = 0;
                  repathSucceeded = true;
                  enemy.greenRerouteCount = (enemy.greenRerouteCount || 0) + 1;
                  enemy.greenRerouteCooldown = 0.75; // wait before next reroute attempt
                  console.log(`[BLOCK] Enemy ${enemy.id} rerouted to waypoint ${waypointIndex} on green path (attempt ${enemy.greenRerouteCount})`);
                  break;
                }
              }
            }
          }
          
          if (!repathSucceeded && (!enemy.greenRerouteCooldown || enemy.greenRerouteCooldown <= 0)) {
            const goalTile = goal.toTile(32);
            const path = this.pathfinder.findPath(
              new Vector2(enemyTile.x, enemyTile.y),
              new Vector2(goalTile.x, goalTile.y),
              state.grid
            );
            
            if (path.length > 0) {
              const touchesBlockedTile = path.some(tilePos => 
                tilePos.x === buildingTile.x && tilePos.y === buildingTile.y
              );
              if (touchesBlockedTile) {
                enemy.greenRerouteCount = (enemy.greenRerouteCount || 0) + 1;
                enemy.greenRerouteCooldown = 0.75;
              } else {
              enemy.path = path.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
              enemy.currentPathIndex = 0;
              repathSucceeded = true;
              enemy.greenRerouteCount = (enemy.greenRerouteCount || 0) + 1;
              enemy.greenRerouteCooldown = 0.75;
              }
            }
          }
          
          if (repathSucceeded) {
            enemy.blockingBuildingTile = null;
            enemy.greenBlockTime = 0;
            enemy.isPhased = false;
            // Keep reroute count so repeated collisions eventually trigger phasing
            if (!enemy.greenRerouteCooldown || enemy.greenRerouteCooldown < 0.5) {
              enemy.greenRerouteCooldown = 0.5;
            }
            continue;
          }
          
          if (enemy.greenBlockTime >= 0.5) {
            // Consider enemy stuck -> temporarily phase through building
            enemy.isPhased = true;
            enemy.blockingBuildingTile = null;
            enemy.greenBlockTime = 0;
            console.log(`[BLOCK] Enemy ${enemy.id} stuck at (${buildingTile.x}, ${buildingTile.y}) on green path - enabling temporary phase`);
            continue;
          }
          
          // Wait a bit longer before phasing; keep them stopped for now
          enemy.path = [];
          enemy.currentPathIndex = 0;
          continue;
        }
        
        enemy.greenBlockTime = 0;
        
        if (!enemy.isBlockBreaker && distanceToBuilding <= 1) {
          // Enemy is at/adjacent to building - IMMEDIATELY activate block breaker mode
          enemy.path = [];
          enemy.currentPathIndex = 0;
          
          // Activate block breaker mode immediately
          enemy.isBlockBreaker = true;
          enemy.blockBreakerTarget = enemy.blockingBuildingTile.clone();
          enemy.attackCooldown = 0;
          enemy.speed = enemy.baseSpeed * 1.3; // +30% speed boost
          enemy.greenBlockTime = 0;
          
          const enemyTypeName = enemy.type.charAt(0).toUpperCase() + enemy.type.slice(1);
          this.logMessages.addMessage(
            `Block Breaker: ${enemyTypeName} attacking building at (${buildingTile.x}, ${buildingTile.y})`
          );
          console.log(`[BLOCK] Enemy ${enemy.id} immediately entering block breaker mode for building at (${buildingTile.x}, ${buildingTile.y})`);
        } else if (isBuildingStillThere) {
          // Enemy moved away from building - clear state
          console.log(`[BLOCK] Enemy ${enemy.id} moved away from building, clearing state`);
          enemy.blockingBuildingTile = null;
          enemy.greenBlockTime = 0;
          enemy.greenRerouteCount = 0;
          enemy.greenRerouteCooldown = 0;
          enemy.isPhased = false;
        }
      }

      // If path is valid, ALWAYS force enemies to use the global path
      // BUT: Skip if enemy has already reached goal, is in block breaker mode, or is on a building
      // Ghost enemies use pathfinding (but can pass through buildings)
      if (enemy.type === 'ghost' && !this.enemySystem.hasReachedGoal(enemy, goal)) {
        // Always pathfind directly to goal, ignoring buildings
        const enemyTile = enemy.position.toTile(32);
        const goalTile = goal.toTile(32);
        
        // Check if enemy needs a path or if goal position changed
        const goalPixel = Vector2.fromTileCentered(goalTile.x, goalTile.y, 32);
        const needsPath = enemy.path.length === 0 || 
                         (enemy.path.length > 0 && 
                          enemy.path[enemy.path.length - 1].distance(goalPixel) > 1);
        
        if (needsPath) {
          // Pathfind directly to goal (ignoring buildings - ghost can pass through)
          const path = this.pathfinder.findPath(
            new Vector2(enemyTile.x, enemyTile.y),
            new Vector2(goalTile.x, goalTile.y),
            state.grid,
            true // ignoreBuildings = true for ghost
          );
          
          if (path.length > 0) {
            enemy.path = path.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
            
            // Find closest waypoint
            let bestIndex = 0;
            let minDist = Infinity;
            for (let i = 0; i < enemy.path.length; i++) {
              const dist = enemy.position.distance(enemy.path[i]);
              if (dist < minDist) {
                minDist = dist;
                bestIndex = i;
              }
            }
            
            // Advance to next waypoint if already at current one
            if (minDist < 1.0 && bestIndex < enemy.path.length - 1) {
              bestIndex = bestIndex + 1;
            }
            
            enemy.currentPathIndex = bestIndex;
            enemy.lastPosition = enemy.position.clone();
            console.log(`[PATH] Assigned direct path to goal for ${enemy.type} enemy ${enemy.id}, path.length=${path.length}, waypoint=${bestIndex}, pos=(${enemy.position.x.toFixed(1)}, ${enemy.position.y.toFixed(1)})`);
          } else {
            // Fallback: create direct path if pathfinding fails
            console.warn(`[PATH] Pathfinding failed for ${enemy.type} enemy ${enemy.id}, using direct path from (${enemy.position.x.toFixed(1)}, ${enemy.position.y.toFixed(1)}) to (${goalPixel.x.toFixed(1)}, ${goalPixel.y.toFixed(1)})`);
            enemy.path = [enemy.position.clone(), goalPixel];
            // If enemy is already at or very close to start position, advance to goal
            const distToStart = enemy.position.distance(enemy.path[0]);
            if (distToStart < 1.0 && enemy.path.length > 1) {
              enemy.currentPathIndex = 1; // Start moving towards goal immediately
            } else {
              enemy.currentPathIndex = 0;
            }
            enemy.lastPosition = enemy.position.clone();
            console.log(`[PATH] Direct path set: path.length=${enemy.path.length}, waypoint=${enemy.currentPathIndex}, distToStart=${distToStart.toFixed(2)}`);
          }
        } else {
          // Path exists, but ensure currentPathIndex is valid
          if (enemy.currentPathIndex >= enemy.path.length && enemy.path.length > 0) {
            enemy.currentPathIndex = enemy.path.length - 1;
          }
          // Also ensure path isn't empty - if it is, reassign
          if (enemy.path.length === 0) {
            const path = this.pathfinder.findPath(
              new Vector2(enemyTile.x, enemyTile.y),
              new Vector2(goalTile.x, goalTile.y),
              state.grid,
              true // ignoreBuildings = true
            );
            if (path.length > 0) {
              enemy.path = path.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
              enemy.currentPathIndex = 0;
              enemy.lastPosition = enemy.position.clone();
              console.log(`[PATH] Reassigned path for ${enemy.type} enemy ${enemy.id} (path was empty)`);
            } else {
              // Ultimate fallback
              enemy.path = [enemy.position.clone(), goalPixel];
              enemy.currentPathIndex = 0;
              enemy.lastPosition = enemy.position.clone();
            }
          }
        }
        // Don't continue - ghost enemies still need to reach updateEnemy call below
      }
      
      // Flying enemies: Skip path logic - they move directly towards goal in EnemySystem
      // Don't continue - flying enemies still need to reach updateEnemy call below
      
      // Ground unit path logic (ghost/flying handled above)
      // SIMPLIFIED: Only assign path if enemy has NO path
      // Let enemies keep their path once assigned - they'll naturally hit buildings and stop
      if (enemy.type !== 'ghost' && enemy.type !== 'flying' && enemy.path.length === 0) {
        // Only assign path if:
        // - Enemy doesn't have a path
        // - Enemy is not in block breaker mode
        // - Enemy is not stopped at a blocking building
        if (!enemy.isBlockBreaker && !enemy.blockingBuildingTile && !this.enemySystem.hasReachedGoal(enemy, goal)) {
          if (this.globalPath.length > 0) {
            // Assign global path (even if red). Enemies will follow it until they physically touch a building.
            enemy.path = this.globalPath.map(p => p.clone());
            
            // Find closest waypoint
            let bestIndex = 0;
            let minDist = Infinity;
            for (let i = 0; i < enemy.path.length; i++) {
              const dist = enemy.position.distance(enemy.path[i]);
              if (dist < minDist) {
                minDist = dist;
                bestIndex = i;
              }
            }
            
            // If very close to waypoint, advance to next
            if (minDist < 1.0 && bestIndex < enemy.path.length - 1) {
              bestIndex = bestIndex + 1;
            }
            
            enemy.currentPathIndex = bestIndex;
            enemy.lastPosition = enemy.position.clone();
            enemy.idleTime = 0;
            console.log(`[PATH] Assigned path to enemy ${enemy.id}, path.length=${enemy.path.length}, waypoint=${bestIndex}`);
          }
        }
      }

      // REMOVED: Don't exit block breaker mode when path becomes valid
      // Enemies should finish destroying the building they're attacking
      
      // NOTE: Path assignment is now handled above in simplified logic (lines 2148-2181)
      // This duplicate fallback section is REMOVED to prevent constant path reassignment
      if (false && this.isPathValid && enemy.path.length === 0 && enemy.type !== 'ghost' && enemy.type !== 'flying' && 
          !enemy.isBlockBreaker && !enemy.blockingBuildingTile) {
        // Check if enemy is at a blocking building - if so, don't assign path (they should enter block breaker mode)
        const enemyTile = enemy.position.toTile(32);
        const isAtBlockingBuilding = this.blockingBuildings.some(b => {
          const distance = Math.abs(enemyTile.x - b.x) + Math.abs(enemyTile.y - b.y);
          return distance <= 1;
        });
        
        if (!isAtBlockingBuilding) {
          // Enemy is not at a blocking building - assign path
          // Path is valid but enemy has no path - assign global path immediately
          // This can happen at game start, when enemies are created before path is calculated, or when they hit local buildings
          if (this.globalPath.length > 0) {
            enemy.path = this.globalPath.map(p => p.clone());
            // Find closest waypoint, but prefer waypoints ahead of current progress
            const oldPathIndex = enemy.currentPathIndex;
            let bestIndex = 0;
            let minDist = Infinity;
            
            // Prefer waypoints ahead of current progress to prevent backwards movement
            if (oldPathIndex > 0 && oldPathIndex < enemy.path.length) {
              const searchStart = Math.max(0, oldPathIndex - 1);
              for (let i = searchStart; i < enemy.path.length; i++) {
                const dist = enemy.position.distance(enemy.path[i]);
                if (dist < minDist) {
                  minDist = dist;
                  bestIndex = i;
                }
              }
            } else {
              for (let i = 0; i < enemy.path.length; i++) {
                const dist = enemy.position.distance(enemy.path[i]);
                if (dist < minDist) {
                  minDist = dist;
                  bestIndex = i;
                }
              }
            }
            
            // If enemy is at or very close to the waypoint (within 1 pixel), advance to next waypoint
            if (minDist < 1.0 && bestIndex < enemy.path.length - 1) {
              bestIndex = bestIndex + 1;
            }
            
            // Ensure we don't move backwards - if the best waypoint is before the old index, use old index
            if (oldPathIndex > 0 && bestIndex < oldPathIndex && oldPathIndex < enemy.path.length) {
              const distToOldWaypoint = enemy.position.distance(enemy.path[oldPathIndex]);
              if (distToOldWaypoint < 64) { // 2 tiles = 64 pixels
                bestIndex = oldPathIndex; // Keep moving forward
              }
            }
            
            enemy.currentPathIndex = bestIndex;
            enemy.lastPosition = enemy.position.clone();
            enemy.idleTime = 0;
            enemy.blockingBuildingTile = null; // Clear blocking building tile when path is recalculated
            console.log(`[PATH] Assigned global path to enemy ${enemy.id}, path.length=${enemy.path.length}, waypoint=${bestIndex}`);
          } else {
            // Global path not ready - try to pathfind directly to goal
            const goal = state.goals[0];
            const goalTile = goal.toTile(32);
            const path = this.pathfinder.findPath(
              new Vector2(enemyTile.x, enemyTile.y),
              new Vector2(goalTile.x, goalTile.y),
              state.grid
            );
            if (path.length > 0) {
              enemy.path = path.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
              enemy.currentPathIndex = 0;
              enemy.lastPosition = enemy.position.clone();
              enemy.idleTime = 0;
              enemy.blockingBuildingTile = null;
              console.log(`[PATH] Assigned direct path to enemy ${enemy.id} (global path not ready)`);
            }
          }
        }
      }
      
      // REMOVED: Don't exit block breaker mode when path becomes valid
      // Enemies should finish destroying the building they're attacking
      
      // Handle block breaker attacks BEFORE updateEnemy (so attacks happen even if path is cleared)
      if (enemy.isBlockBreaker && enemy.blockBreakerTarget) {
        this.handleBlockBreakerAttack(enemy, deltaTime, state);
      } else if (enemy.isBlockBreaker && !enemy.blockBreakerTarget) {
        console.log(`[ERROR] Enemy ${enemy.id} isBlockBreaker=true but blockBreakerTarget is null!`);
      }

      // Update enemy movement (but don't let it interfere with block breaker mode)
      this.enemySystem.updateEnemy(enemy, deltaTime, goal, state.grid, this.blockingBuildings);
      
      // After updateEnemy, ensure block breaker enemies stay stopped at their target
      if (enemy.isBlockBreaker && enemy.blockBreakerTarget) {
        const enemyTileAfterUpdate = enemy.position.toTile(32);
        const distanceToTarget = Math.abs(enemyTileAfterUpdate.x - enemy.blockBreakerTarget.x) + 
                                 Math.abs(enemyTileAfterUpdate.y - enemy.blockBreakerTarget.y);
        if (distanceToTarget <= 1) {
          // Enemy is at target - ensure path is cleared so they stay put
          enemy.path = [];
          enemy.currentPathIndex = 0;
        }
      } else if (enemy.isPhased) {
        // If enemy was phased due to a green-path collision, undo phasing once clear
        const enemyTileAfterUpdate = enemy.position.toTile(32);
        if (enemyTileAfterUpdate.y >= 0 && enemyTileAfterUpdate.y < state.grid.length &&
            enemyTileAfterUpdate.x >= 0 && enemyTileAfterUpdate.x < state.grid[enemyTileAfterUpdate.y].length) {
          const tile = state.grid[enemyTileAfterUpdate.y][enemyTileAfterUpdate.x];
          if (tile.type !== 'wall' && tile.type !== 'tower') {
            enemy.isPhased = false;
          }
        } else {
          enemy.isPhased = false;
        }
      }
    }
    
    // Remove dead/reached goal enemies
    for (const id of enemiesToRemove) {
      this.enemySystem.removeEnemy(id);
    }
    
    // Update HUD after enemy processing (to reflect health changes from enemies reaching goal)
    this.hud.update(this.gameState.getState(), this.gameTimer, this.totalKills, this.totalDamage);
    
    // Force repath only if enemies are in contact with buildings
    if (needsRepath) {
      this.forceEnemyRepath();
    }

    // Update AoE effects
    for (let i = this.aoeEffects.length - 1; i >= 0; i--) {
      const effect = this.aoeEffects[i];
      effect.duration -= deltaTime;
      
      if (effect.duration <= 0) {
        this.aoeEffects.splice(i, 1);
        continue;
      }
      
      // Expand radius over time (fast expansion at start, slower at end)
      const progress = 1 - (effect.duration / effect.maxDuration);
      effect.radius = effect.maxRadius * Math.pow(progress, 0.5); // Square root for smooth expansion
    }

    // Update projectiles
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      projectile.lifetime -= deltaTime;
      
      if (projectile.lifetime <= 0) {
        // Projectile reached target, create AoE effect if cannon
        if (projectile.towerType === 'cannon' && projectile.aoeRadius) {
          this.createAoeEffect(projectile.target, projectile.color, projectile.aoeRadius);
        }
        this.projectiles.splice(i, 1);
        continue;
      }
      
      // Move projectile towards target
      const direction = projectile.target.subtract(projectile.position);
      const distance = direction.distance(Vector2.zero());
      const moveDistance = projectile.speed * deltaTime;
      
      if (distance <= moveDistance) {
        // Reached target
        this.audioSystem?.playSound('projectile_hit');
        // Create AoE effect if cannon
        if (projectile.towerType === 'cannon' && projectile.aoeRadius) {
          this.createAoeEffect(projectile.target, projectile.color, projectile.aoeRadius);
        }
        this.projectiles.splice(i, 1);
      } else {
        // Move towards target
        const normalized = direction.normalize();
        projectile.position = projectile.position.add(normalized.multiply(moveDistance));
      }
    }

    // Update towers
    for (const tower of this.towerSystem.getAllTowers()) {
      // Update arrow tower B passive ability - Rapid Fire (first shot triggers, then every 20s, +150% attack speed for 6s)
      if (tower.type === 'arrow' && tower.hasUpgradeB) {
        const currentTime = this.lastTime;
        
        // Initialize lastAbilityTime as -1 (not yet triggered) if undefined
        if (tower.lastAbilityTime === undefined) {
          tower.lastAbilityTime = -1; // -1 means not yet triggered (waiting for first shot)
          console.log(`[ARROW DEBUG] Initialized lastAbilityTime to -1 for tower ${tower.id}`);
        }
        
        // If lastAbilityTime is -1, ability will trigger on first shot (handled below when tower fires)
        // Otherwise, check time-based activation
        if (tower.lastAbilityTime >= 0) {
          const timeSinceLastAbility = currentTime - tower.lastAbilityTime;
          
          // Ability is active - check if it should deactivate (after 6 seconds)
          if (tower.abilityActive && timeSinceLastAbility >= 6) {
            // Deactivate ability and reset timer for next cooldown
            tower.abilityActive = false;
            tower.attackSpeed = tower.baseAttackSpeed || 1.5; // Return to base speed
            tower.lastAbilityTime = currentTime; // Reset timer to start cooldown
            
            console.log(`[ARROW ABILITY] Passive ended. Attack speed: ${tower.attackSpeed}s. Cooldown started.`);
          }
          // Ability is on cooldown - check if it should activate (14 seconds after deactivation = 20s total cycle)
          else if (!tower.abilityActive && timeSinceLastAbility >= 14) {
            // Activate ability
            tower.abilityActive = true;
            tower.attackSpeed = (tower.baseAttackSpeed || 1.5) * 2.5; // +150% attack speed = 2.5x attacks per second
            tower.lastAbilityTime = currentTime; // Reset timer for ability duration
            
            console.log(`[ARROW ABILITY] Passive activated! Attack speed: ${tower.attackSpeed}s for 6s`);
            this.logMessages?.addMessage('Arrow Tower: Rapid Fire!');
          }
        }
      }
      
      // Skip towers that are being repaired
      if (this.repairSystem.isRepairing(tower.id)) {
        continue;
      }
      
      const targets = this.towerSystem.findTargets(tower, this.enemySystem.getAllEnemies());
      
      if (targets.length > 0 && this.towerSystem.canFire(tower, this.lastTime)) {
        const target = this.towerSystem.selectTarget(tower, targets);
        if (target) {
          // Debug: Check tower state before firing
          if (tower.type === 'arrow' && tower.hasUpgradeB) {
            console.log(`[ARROW DEBUG] Before firing: lastAbilityTime=${tower.lastAbilityTime}, abilityActive=${tower.abilityActive}, type=${typeof tower.lastAbilityTime}`);
          }
          
          // Trigger arrow tower B ability (Rapid Fire) on first shot
          if (tower.type === 'arrow' && tower.hasUpgradeB && tower.lastAbilityTime === -1) {
            tower.abilityActive = true;
            tower.attackSpeed = (tower.baseAttackSpeed || 1.5) * 2.5; // +150% attack speed = 2.5x attacks per second
            tower.lastAbilityTime = this.lastTime;
            console.log(`[ARROW ABILITY] First shot - Rapid Fire activated! Attack speed: ${tower.attackSpeed}s for 6s`);
            this.logMessages?.addMessage('Arrow Tower: Rapid Fire!');
          }
          
          const result = this.towerSystem.fire(tower, target, this.enemySystem.getAllEnemies(), this.lastTime);
          
          // Handle miss (evaded attack)
          if (result.evaded) {
            this.damageNumbers.spawnMissNumber(target.position);
            // Play miss sound (max 3 concurrent)
            this.audioSystem?.playSound('attack_miss', 0.5); // Lower volume for misses
          }
          
          // Handle executed targets (sniper instant kill)
          if (result.executedTargets && result.executedTargets.length > 0) {
            for (const executedTarget of result.executedTargets) {
              this.damageNumbers.spawnExecutedNumber(result.damage, executedTarget.position);
              // Play special execution sound
              this.audioSystem?.playSound('sniper_execute', 0.8);
            }
          }
          
          // Handle armor reduction indicator (cannon upgrade B)
          if (result.armorReductionTargets && result.armorReductionTargets.length > 0) {
            for (const armorTarget of result.armorReductionTargets) {
              this.damageNumbers.spawnArmorReductionNumber(0.15, armorTarget.position); // 15% armor reduction
            }
          }
          
          // Spawn crit damage number if it was a critical hit (and not executed)
          if (result.wasCrit && result.critDamage && (!result.executedTargets || result.executedTargets.length === 0)) {
            this.damageNumbers.spawnCritNumber(result.critDamage, target.position);
            // Play crit sound (max 3 concurrent)
            this.audioSystem?.playSound('crit_hit', 0.7);
          }
          
          // Play tower fire sound (max 3 per tower type)
          const soundKey = `tower_${tower.type}_fire`;
          const currentCount = this.towerAttackSoundCounts.get(soundKey) || 0;
          if (currentCount < 3) {
            let soundName = '';
            let pitch = 1.0; // Normal pitch
            
            if (tower.type === 'arrow') {
              soundName = 'tower_arrow_fire';
              // Higher pitch during rapid fire (ability active on Upgrade B)
              if (tower.hasUpgradeB && tower.abilityActive) {
                pitch = 1.3; // 30% higher pitch during rapid fire
              }
            } else if (tower.type === 'cannon') {
              soundName = 'tower_cannon_fire';
            } else if (tower.type === 'sniper') {
              soundName = 'tower_sniper_fire';
            }
            
            if (soundName && this.audioSystem) {
              this.towerAttackSoundCounts.set(soundKey, currentCount + 1);
              this.audioSystem.playSound(soundName, 1.0, pitch);
              // Decrement count after sound duration (approximately 0.3 seconds)
              setTimeout(() => {
                const count = this.towerAttackSoundCounts.get(soundKey) || 0;
                if (count > 0) {
                  this.towerAttackSoundCounts.set(soundKey, count - 1);
                }
              }, 300);
            }
          }
          
          // Create projectile for towers that have visual projectiles
          if (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper') {
            const towerPixelPos = Vector2.fromTile(tower.position.x, tower.position.y, 32);
            const targetPos = target.position.clone();
            const direction = targetPos.subtract(towerPixelPos);
            const distance = direction.distance(Vector2.zero());
            const speed = 500; // pixels per second
            const lifetime = distance / speed;
            
            // Set projectile color based on tower type
            let projectileColor = 0xffffff; // Default white
            if (tower.type === 'arrow') {
              projectileColor = 0xffffff; // White
            } else if (tower.type === 'cannon') {
              projectileColor = 0xff0000; // Red
            } else if (tower.type === 'sniper') {
              projectileColor = 0x0000ff; // Blue
            }
            
            console.log(`[PROJECTILE] Creating ${tower.type} projectile: aoeRadius=${tower.aoeRadius}, range=${tower.range}`);
            
            this.projectiles.push({
              position: towerPixelPos.clone(),
              target: targetPos,
              speed: speed,
              lifetime: lifetime,
              color: projectileColor,
              towerType: tower.type,
              aoeRadius: tower.aoeRadius, // Store AoE radius for visual effect
            });
          }
          
          // Apply damage and track for MVP
          let totalDamageDealt = 0;
          for (const hitTarget of result.targets) {
            // Check if this target was executed (instant kill)
            const wasExecuted = result.executedTargets?.includes(hitTarget);
            
            if (wasExecuted) {
              // Instant kill - bypass resistances and set HP to 0
              const executeDamage = hitTarget.hp;
              hitTarget.hp = 0;
              this.totalDamage += executeDamage;
              totalDamageDealt += executeDamage;
              console.log(`[EXECUTE] Enemy ${hitTarget.id} executed! Dealt ${Math.floor(executeDamage)} damage (instant kill)`);
            } else {
              // Normal damage application
              const targetDamage = result.targetDamages?.get(hitTarget.id) ?? result.damage;
              this.enemySystem.takeDamage(hitTarget, targetDamage, result.damageType);
              this.totalDamage += targetDamage;
              totalDamageDealt += targetDamage;
            }
            
            // Apply status effects
            if (result.effects) {
              for (const effect of result.effects) {
                this.enemySystem.applyStatusEffect(hitTarget, {
                  type: effect.type as any,
                  duration: effect.duration,
                  value: effect.value,
                });
              }
            }
          }
          
          // Track damage for MVP system
          if (totalDamageDealt > 0) {
            const currentDamage = this.towerDamageMap.get(tower.id) || 0;
            this.towerDamageMap.set(tower.id, currentDamage + totalDamageDealt);
            this.updateMVPIndicators();
          }
        }
      }
    }

    // Check wave completion
    // Only complete wave if all enemies have been spawned AND all are dead/removed
    if (this.isWaveActive) {
      const remainingEnemies = this.enemySystem.getAllEnemies();
      // Wait for all enemies to spawn before checking completion
      if (this.spawnedEnemyCount >= this.expectedEnemyCount && remainingEnemies.length === 0) {
        this.completeWave();
      }
    } else if (this.autoWave && !this.autoWaveCountdownStarted) {
      // If auto mode is active but wave is not active, check if we should start countdown
      // This handles the case where enemies were cleared after completeWave() returned early
      const remainingEnemies = this.enemySystem.getAllEnemies();
      if (remainingEnemies.length === 0) {
        // Enemies are cleared, start countdown if not already started
        this.autoWaveCountdownStarted = true;
        this.startWaveCountdown();
      }
    }

    // Render (only dynamic elements update every frame, static map only when needed)
    this.renderMap();
    // Update ghost preview
    this.updateGhostPreview();
    // Update log messages
    this.logMessages.update();
  }

  private startWave(): void {
    if (this.isWaveActive) return;

    this.autoWaveCountdownStarted = false; // Reset countdown flag when wave starts
    if (this.buildMode) {
      this.cancelBuildMode();
      this.logMessages.addMessage('Build mode cancelled: wave started.');
    }
    const waveNumber = this.gameState.getWaveNumber() + 1;
    this.gameState.setWaveNumber(waveNumber);
    this.audioSystem?.playSound('wave_start');
    
    // Ensure global path is calculated before spawning enemies
    this.updateGlobalPath();
    
    const waveData = this.waveSystem.generateWave(waveNumber, this.gameState.isSurvivalMode());
    const state = this.gameState.getState();
    const spawn = state.spawns[0];

    // Update wave info panel
    this.waveInfoPanel.updateWave(waveData, waveNumber);

    // Track expected enemy count for wave completion check
    this.expectedEnemyCount = waveData.enemyCount;
    this.spawnedEnemyCount = 0;

    console.log(`Starting wave ${waveNumber} with ${waveData.enemyCount} enemies, globalPath.length=${this.globalPath.length}, isPathValid=${this.isPathValid}`);

    // Spawn enemies with 0.35 second intervals
    for (let i = 0; i < waveData.enemyCount; i++) {
      setTimeout(() => {
      const enemyType = waveData.enemyTypes[Math.floor(Math.random() * waveData.enemyTypes.length)];
      const isElite = Math.random() < 0.05 && waveNumber >= 25; // 5% elite chance after wave 25
      
      const enemy = this.enemySystem.createEnemy(
        enemyType,
        spawn.clone(),
        waveNumber,
        this.gameState.isSurvivalMode(),
        isElite
      );
        this.spawnedEnemyCount++; // Track that an enemy has been spawned
        console.log(`[SPAWN] Enemy ${enemy.id} (${enemyType}) spawned at tile (${spawn.x}, ${spawn.y}), pixel (${enemy.position.x.toFixed(1)}, ${enemy.position.y.toFixed(1)}), total enemies: ${this.enemySystem.getAllEnemies().length}`);
        this.audioSystem?.playSound('enemy_spawn');

      // Set path - flying and ghost move directly (handled in EnemySystem), others get global path
      if (enemy.type === 'flying' || enemy.type === 'ghost') {
        // Flying and ghost enemies: No path needed - they move directly towards goal in EnemySystem
        // Set a simple direct path for visualization only
        const goal = state.goals[0];
        const goalPixel = Vector2.fromTileCentered(goal.x, goal.y, 32);
        enemy.path = [enemy.position.clone(), goalPixel];
        enemy.currentPathIndex = 0;
        enemy.lastPosition = enemy.position.clone();
        console.log(`[SPAWN] ${enemy.type} enemy ${enemy.id} - will move directly to goal (straight line)`);
      } else if (this.globalPath.length > 0) {
        // Ground enemies use global path
        enemy.path = this.globalPath.map(p => p.clone());
        // Find closest waypoint, but if enemy is at or very close to a waypoint, use the NEXT one
        let bestIndex = 0;
        let minDist = Infinity;
        for (let i = 0; i < enemy.path.length; i++) {
          const dist = enemy.position.distance(enemy.path[i]);
          if (dist < minDist) {
            minDist = dist;
            bestIndex = i;
          }
        }
        // If enemy is at or very close to the waypoint (within 1 pixel), advance to next waypoint
        if (minDist < 1.0 && bestIndex < enemy.path.length - 1) {
          bestIndex = bestIndex + 1;
        }
        enemy.currentPathIndex = bestIndex;
        // Initialize lastPosition to current position to prevent false idle detection
        enemy.lastPosition = enemy.position.clone();
        enemy.idleTime = 0;
      } else {
        // Global path not ready yet - will be assigned in update loop
        enemy.path = [];
        enemy.currentPathIndex = 0;
        enemy.lastPosition = enemy.position.clone();
        enemy.idleTime = 0;
      }
      }, i * 350); // 0.35 second (350ms) interval between spawns
    }

    this.isWaveActive = true;
    console.log(`Wave ${waveNumber} started with ${this.enemySystem.getAllEnemies().length} enemies (${this.expectedEnemyCount} expected to spawn)`);
  }

  private completeWave(): void {
    this.isWaveActive = false;
    this.expectedEnemyCount = 0;
    this.spawnedEnemyCount = 0;
    const waveNumber = this.gameState.getWaveNumber();
    this.audioSystem?.playSound('wave_complete');
    
    console.log(`Wave ${waveNumber} completed!`);
    
    const waveBonus = this.economySystem.awardWaveBonus(waveNumber);
    this.logMessages.addMessage(`+${waveBonus}g (wave bonus)`);
    this.economySystem.updateGPM(waveNumber);
    this.economySystem.checkGoldFreeze(waveNumber);
    
    // Check for boss wave completion
    if (waveNumber === GAME_CONSTANTS.TOTAL_WAVES) {
      console.log('Final Boss defeated! Entering Survival Mode...');
      this.gameState.enterSurvivalMode();
      // In full implementation, show prompt here
    }

    // Auto start next wave if auto mode is enabled
    if (this.autoWave) {
      // Check if enemies are still alive before starting countdown
      const remainingEnemies = this.enemySystem.getAllEnemies();
      if (remainingEnemies.length > 0) {
        this.logMessages.addMessage(`Auto mode: Waiting for ${remainingEnemies.length} enemies to be defeated`);
        this.autoWaveCountdownStarted = false; // Reset flag so we can start countdown when enemies are cleared
        return;
      }
      
      // Start countdown before next wave (only if not already started)
      if (!this.autoWaveCountdownStarted) {
        this.autoWaveCountdownStarted = true;
        this.startWaveCountdown();
      }
    }
  }

  public startNextWave(skipTowerCheck: boolean = false): void {
    if (!skipTowerCheck && !this.hasAnyTowersPlaced()) {
      this.warnNoTowers('start_wave');
      this.disableAutoModeDueToNoTowers();
      return;
    }
    
    // Check if there are still enemies alive from previous wave
    const remainingEnemies = this.enemySystem.getAllEnemies();
    if (remainingEnemies.length > 0) {
      this.logMessages.addMessage(`Cannot start wave: ${remainingEnemies.length} enemies still alive`);
      return;
    }

    if (!this.isWaveActive) {
      this.startWave();
    }
  }

  private updateGlobalPath(): void {
    // Calculate global path from spawn to goal
    const state = this.gameState.getState();
    if (state.spawns.length === 0 || state.goals.length === 0) {
      this.globalPath = [];
      this.isPathValid = false;
      this.blockingBuildings = [];
      return;
    }

    const spawn = state.spawns[0];
    const goal = state.goals[0];
    
    // Find path in tile coordinates
    const tilePath = this.pathfinder.findPath(spawn, goal, state.grid);
    
    if (tilePath.length > 0) {
      // Path is valid
      const newPath = tilePath.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
      const wasBlocked = !this.isPathValid;
      this.globalPath = newPath;
      this.isPathValid = true;
      this.lastValidPath = newPath.map(p => p.clone());
      this.blockedHighlightTile = null;
      this.lastPlacementTile = null;
      
      if (wasBlocked) {
        // Path was blocked, now it's valid - clear blocking buildings
        this.blockingBuildings = [];
        console.log('[PATH] ✅ Path is now VALID (green line) - cleared blocking buildings');
        console.log('[PATH] Enemies should now exit block breaker mode and resume normal movement');
      }
    } else {
      // Path is blocked
      const wasValid = this.isPathValid;
        this.isPathValid = false;
      
      if (wasValid || this.blockingBuildings.length === 0) {
        // Path just became blocked, or we don't have blocking buildings yet
        // Find ALL blocking buildings
        this.blockingBuildings = this.pathfinder.getAllBlockingBuildings(spawn, goal, state.grid);
        console.log(`[PATH] Path blocked by ${this.blockingBuildings.length} building(s):`, 
          this.blockingBuildings.map(b => `(${b.x}, ${b.y})`).join(', '));
        
        // DON'T clear enemy paths - let them continue moving until they naturally hit the blocking building
        // Collision detection in EnemySystem will stop them, then they'll enter block breaker mode
        if (wasValid) {
          console.log(`[PATH] Path just became blocked! Enemies will continue moving until they hit blocking buildings.`);
        }
      }
      
      if (this.blockingBuildings.length > 0) {
        let highlight: Vector2 | null = null;
        if (this.lastPlacementTile) {
          const match = this.blockingBuildings.find(b => 
            b.x === this.lastPlacementTile!.x && b.y === this.lastPlacementTile!.y
          );
          if (match) {
            highlight = match.clone();
          }
        }
        if (!highlight) {
          highlight = this.blockingBuildings[0].clone();
        }
        this.blockedHighlightTile = highlight;
      } else {
        this.blockedHighlightTile = null;
      }
      this.lastPlacementTile = null;
      
      // Keep using last valid path (red line) for visualization only
      if (this.lastValidPath.length === 0) {
        this.globalPath = [];
      } else {
        this.globalPath = this.lastValidPath.map(p => p.clone());
      }
    }
  }

  private findLastWalkableTile(enemy: Enemy, grid: any): Vector2 | null {
    // Check if enemy is currently on a building tile
    const currentTile = enemy.position.toTile(32);
    if (currentTile.y >= 0 && currentTile.y < grid.length && 
        currentTile.x >= 0 && currentTile.x < grid[currentTile.y].length) {
      const tile = grid[currentTile.y][currentTile.x];
      if (tile.type === 'wall' || tile.type === 'tower') {
        // Enemy is stuck on a building, find last walkable tile from path history
        // Check previous waypoints in reverse order
        for (let i = enemy.currentPathIndex - 1; i >= 0; i--) {
          if (i < enemy.path.length) {
            const waypointTile = enemy.path[i].toTile(32);
            if (waypointTile.y >= 0 && waypointTile.y < grid.length && 
                waypointTile.x >= 0 && waypointTile.x < grid[waypointTile.y].length) {
              const checkTile = grid[waypointTile.y][waypointTile.x];
              if (checkTile.type !== 'wall' && checkTile.type !== 'tower' && 
                  checkTile.type !== 'border' && checkTile.type !== 'spawn' && 
                  checkTile.type !== 'goal') {
                return enemy.path[i].clone();
              }
            }
          }
        }
        // If no valid previous waypoint, try nearby tiles
        const directions = [
          { x: 0, y: -1 }, { x: 0, y: 1 },
          { x: -1, y: 0 }, { x: 1, y: 0 }
        ];
        for (const dir of directions) {
          const checkX = currentTile.x + dir.x;
          const checkY = currentTile.y + dir.y;
          if (checkY >= 0 && checkY < grid.length && 
              checkX >= 0 && checkX < grid[checkY].length) {
            const checkTile = grid[checkY][checkX];
            if (checkTile.type !== 'wall' && checkTile.type !== 'tower' && 
                checkTile.type !== 'border' && checkTile.type !== 'spawn' && 
                checkTile.type !== 'goal') {
              return Vector2.fromTile(checkX, checkY, 32);
            }
          }
        }
      }
    }
    return null; // Enemy is not stuck, current position is fine
  }

  private forceEnemyRepath(): void {
    // Force all enemies to use the updated global path
    const state = this.gameState.getState();
    
    for (const enemy of this.enemySystem.getAllEnemies()) {
      // Only update if enemy is a ground unit (ghost/flying handle their own routing)
      if (enemy.type !== 'ghost' && enemy.type !== 'flying') {
        // First, ensure enemy is on a walkable tile
        let enemyTile = enemy.position.toTile(32);
        let needsReposition = false;
        
        // Check if enemy is stuck on a building tile
        if (enemyTile.y >= 0 && enemyTile.y < state.grid.length && 
            enemyTile.x >= 0 && enemyTile.x < state.grid[enemyTile.y].length) {
          const currentTile = state.grid[enemyTile.y][enemyTile.x];
          if (currentTile.type === 'wall' || currentTile.type === 'tower') {
            needsReposition = true;
            // Set enemy to phased (ghost) state so it can move through buildings
            enemy.isPhased = true;
            // Reset idle time to prevent immediate force move trigger
            enemy.idleTime = 0;
          }
        }
        
        // If stuck, find closest point on global path
        if (needsReposition && this.globalPath.length > 0) {
          // Find closest point on global path
          let closestPoint: Vector2 | null = null;
          let minDist = Infinity;
          for (const pathPoint of this.globalPath) {
            const dist = enemy.position.distance(pathPoint);
            if (dist < minDist) {
              minDist = dist;
              closestPoint = pathPoint;
            }
          }
          if (closestPoint) {
            // Don't move enemy immediately, let it phase through buildings to reach the path
            // enemy.position = closestPoint.clone();
            // enemyTile = closestPoint.toTile(32);
          }
        }
        
        // Use path - if blocked, use last valid path (enemies will phase through buildings)
        const pathToUse = this.isPathValid ? this.globalPath : this.lastValidPath;
        if (pathToUse.length > 0) {
          enemy.path = pathToUse.map(p => p.clone());
          
          // If path is blocked, enemy should be phased to move through buildings
          // If path is valid again, remove phased state
          if (!this.isPathValid) {
            enemy.isPhased = true;
          } else {
            // Path is valid - check if enemy is on a building tile
            const enemyTile = enemy.position.toTile(32);
            if (enemyTile.y >= 0 && enemyTile.y < state.grid.length && 
                enemyTile.x >= 0 && enemyTile.x < state.grid[enemyTile.y].length) {
              const currentTile = state.grid[enemyTile.y][enemyTile.x];
              if (currentTile.type !== 'wall' && currentTile.type !== 'tower') {
                // Enemy is on a valid tile and path is valid, remove phased state
                enemy.isPhased = false;
              } else {
                // Enemy is still on a building tile, keep phased state
                enemy.isPhased = true;
              }
            }
          }
          
          // Find the best waypoint to continue from (allow backwards movement)
          let bestIndex = 0;
          let minDistance = Infinity;
          
          // Find the closest waypoint (can be backwards if path updated)
          for (let i = 0; i < enemy.path.length; i++) {
            const dist = enemy.position.distance(enemy.path[i]);
            if (dist < minDistance) {
              minDistance = dist;
              bestIndex = i;
            }
          }
          
          // Use the closest waypoint (allow backwards movement when path updates)
          
          // If we're past the closest waypoint (very close or already passed it), use next
          if (bestIndex > 0 && minDistance < 4) {
            // We're essentially on top of this waypoint, use the next one
            if (bestIndex < enemy.path.length - 1) {
              bestIndex = bestIndex + 1;
            }
          }
          
          enemy.currentPathIndex = Math.min(bestIndex, enemy.path.length - 1);
        } else {
          // No path available, clear enemy path
          enemy.path = [];
          enemy.currentPathIndex = 0;
        }
      }
      // Ghost and flying units will recalculate on their own in the next update
    }
  }

  private startWaveCountdown(): void {
    if (!this.hasAnyTowersPlaced()) {
      this.warnNoTowers('start_wave');
      this.disableAutoModeDueToNoTowers();
      return;
    }
    
    // Calculate countdown duration based on game speed: 5s for 1x, 3s for 2x, 1s for 3x
    const countdownDuration = this.gameSpeed === 1.0 ? 5 : this.gameSpeed === 2.0 ? 3 : 1;
    const countdownSteps = countdownDuration;
    
    // Show countdown messages
    for (let i = countdownSteps; i >= 1; i--) {
      setTimeout(() => {
        if (this.autoWave && !this.isWaveActive) {
          // Double-check enemies are still gone before showing countdown
          const stillAlive = this.enemySystem.getAllEnemies();
          if (stillAlive.length > 0) {
            this.logMessages.addMessage(`Auto mode: ${stillAlive.length} enemies still alive, waiting...`);
            return;
          }
          
          if (i > 1) {
            this.logMessages.addMessage(`${i}...`);
          } else {
            this.logMessages.addMessage('1...');
          }
        }
      }, (countdownSteps - i) * 1000);
    }
    
    // Start wave after countdown duration
    setTimeout(() => {
      if (this.autoWave && !this.isWaveActive) {
        // Final check for enemies before starting
        const stillAlive = this.enemySystem.getAllEnemies();
        if (stillAlive.length === 0) {
          if (!this.hasAnyTowersPlaced()) {
            this.warnNoTowers('start_wave');
            this.disableAutoModeDueToNoTowers();
            this.autoWaveCountdownStarted = false;
            return;
          }
          this.autoWaveCountdownStarted = false; // Reset flag for next wave
          this.startNextWave(true);
        } else {
          this.logMessages.addMessage(`Auto mode: ${stillAlive.length} enemies still alive, waiting...`);
          this.autoWaveCountdownStarted = false; // Reset flag so we can retry when enemies are cleared
        }
      }
    }, countdownDuration * 1000);
  }

  private showRestartConfirmation(): void {
    // Remove existing dialog if present
    if (this.confirmDialog) {
      this.uiContainer.removeChild(this.confirmDialog);
      this.confirmDialog = null;
    }

    // Create confirmation dialog
    this.confirmDialog = new PIXI.Container();
    
    // Semi-transparent background
    const bg = new PIXI.Graphics();
    bg.beginFill(0x000000, 0.7);
    bg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    bg.endFill();
    bg.eventMode = 'static';
    this.confirmDialog.addChild(bg);

    // Dialog box
    const dialogBox = new PIXI.Graphics();
    dialogBox.beginFill(0x222222, 0.95);
    dialogBox.drawRect(0, 0, 400, 150);
    dialogBox.endFill();
    dialogBox.lineStyle(3, 0x888888);
    dialogBox.drawRect(0, 0, 400, 150);
    dialogBox.x = (this.app.screen.width - 400) / 2;
    dialogBox.y = (this.app.screen.height - 150) / 2;
    this.confirmDialog.addChild(dialogBox);

    // Warning text
    const warningText = new PIXI.Text('Restart Game?', {
      fontFamily: 'Courier New',
      fontSize: 20,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    warningText.x = dialogBox.x + 200 - warningText.width / 2;
    warningText.y = dialogBox.y + 20;
    this.confirmDialog.addChild(warningText);

    const messageText = new PIXI.Text('You will lose all progress!', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xff6666,
    });
    messageText.x = dialogBox.x + 200 - messageText.width / 2;
    messageText.y = dialogBox.y + 50;
    this.confirmDialog.addChild(messageText);

    // Confirm button
    const confirmButton = new PIXI.Graphics();
    confirmButton.beginFill(0x660000);
    confirmButton.drawRect(0, 0, 120, 40);
    confirmButton.endFill();
    confirmButton.lineStyle(2, 0xffffff);
    confirmButton.drawRect(0, 0, 120, 40);
    confirmButton.x = dialogBox.x + 80;
    confirmButton.y = dialogBox.y + 90;
    confirmButton.eventMode = 'static';
    confirmButton.cursor = 'pointer';
    confirmButton.on('pointerdown', () => {
      this.restartGame();
    });
    this.confirmDialog.addChild(confirmButton);

    const confirmText = new PIXI.Text('Restart', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xffffff,
    });
    confirmText.x = confirmButton.x + 60 - confirmText.width / 2;
    confirmText.y = confirmButton.y + 20 - confirmText.height / 2;
    this.confirmDialog.addChild(confirmText);

    // Cancel button
    const cancelButton = new PIXI.Graphics();
    cancelButton.beginFill(0x444444);
    cancelButton.drawRect(0, 0, 120, 40);
    cancelButton.endFill();
    cancelButton.lineStyle(2, 0x888888);
    cancelButton.drawRect(0, 0, 120, 40);
    cancelButton.x = dialogBox.x + 200;
    cancelButton.y = dialogBox.y + 90;
    cancelButton.eventMode = 'static';
    cancelButton.cursor = 'pointer';
    cancelButton.on('pointerdown', () => {
      this.hideRestartConfirmation();
    });
    this.confirmDialog.addChild(cancelButton);

    const cancelText = new PIXI.Text('Cancel', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xffffff,
    });
    cancelText.x = cancelButton.x + 60 - cancelText.width / 2;
    cancelText.y = cancelButton.y + 20 - cancelText.height / 2;
    this.confirmDialog.addChild(cancelText);

    this.uiContainer.addChild(this.confirmDialog);
  }

  private hideRestartConfirmation(): void {
    if (this.confirmDialog) {
      this.uiContainer.removeChild(this.confirmDialog);
      this.confirmDialog = null;
    }
  }

  private restartGame(): void {
    this.hideRestartConfirmation();
    window.location.reload();
  }

  private startRepair(item: Tower | Wall): void {
    const state = this.gameState.getState();
    let buildingCost = 0;
    let repairTarget: { id: string; type: 'tower' | 'wall'; hp: number; maxHp: number };

    if ('type' in item && (item as any).type && ['arrow', 'cannon', 'sniper', 'fire', 'ice', 'lightning'].includes((item as any).type)) {
      // It's a tower
      const tower = item as Tower;
      const baseCost = TOWER_STATS[tower.type].cost;
      let totalCost = baseCost;
      if (tower.hasUpgradeA) totalCost += UPGRADE_A_STATS[tower.type].cost;
      if (tower.hasUpgradeB) totalCost += UPGRADE_B_STATS[tower.type].cost;
      if (tower.hasUpgradeC && (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper')) {
        totalCost += UPGRADE_C_STATS[tower.type].cost;
      }
      buildingCost = totalCost;
      repairTarget = { id: tower.id, type: 'tower', hp: tower.hp, maxHp: tower.maxHp };
    } else {
      // It's a wall
      const wall = item as Wall;
      buildingCost = GAME_CONSTANTS.WALL_COST;
      repairTarget = { id: wall.id, type: 'wall', hp: wall.hp, maxHp: wall.maxHp };
    }

    // Check if already at full HP
    if (repairTarget.hp >= repairTarget.maxHp) {
      this.audioSystem?.playSound('invalid_placement');
      this.logMessages.addMessage('Building is already at full HP.');
      return;
    }

    // Check if already repairing
    if (this.repairSystem.isRepairing(repairTarget.id)) {
      this.audioSystem?.playSound('invalid_placement');
      this.logMessages.addMessage('Building is already being repaired.');
      return;
    }

    // Start repair and get cost
    const result = this.repairSystem.startRepair(repairTarget, buildingCost);
    if (!result.success) {
      this.audioSystem?.playSound('invalid_placement');
      this.logMessages.addMessage('Cannot start repair.');
      return;
    }

    // Check if can afford
    if (result.cost && !this.economySystem.canAfford(result.cost)) {
      this.repairSystem.cancelRepair(repairTarget.id);
      this.audioSystem?.playSound('invalid_placement');
      this.logMessages.addMessage(`Not enough gold for repair (need ${result.cost}g).`);
      return;
    }

    // Deduct cost
    if (result.cost) {
      this.economySystem.spend(result.cost);
      const missingHpPercent = (repairTarget.maxHp - repairTarget.hp) / repairTarget.maxHp;
      const duration = Math.max(0.1, missingHpPercent * 10);
      this.logMessages.addMessage(`Repair started: -${result.cost}g (${duration.toFixed(1)}s)`);
      this.audioSystem?.playSound('tower_place');
    }

    // Update UI
    if (this.selectedTower && 'type' in item && (item as any).type) {
      const canAffordA = this.economySystem.canAfford(UPGRADE_A_STATS[(item as Tower).type].cost);
      const canAffordB = this.economySystem.canAfford(UPGRADE_B_STATS[(item as Tower).type].cost);
      const tower = item as Tower;
      const canAffordC = (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper')
        ? this.economySystem.canAfford(UPGRADE_C_STATS[tower.type].cost)
        : false;
      this.towerInfoPanel.refresh(tower, canAffordA, canAffordB, canAffordC, false);
    } else if (this.selectedWall) {
      this.towerInfoPanel.refreshWall(true, this.selectedWall);
    }
  }

  private handleBlockBreakerAttack(enemy: Enemy, deltaTime: number, state: any): void {
    if (!enemy.blockBreakerTarget) {
      console.log(`[ATTACK] Enemy ${enemy.id} has no blockBreakerTarget`);
      return;
    }
    
    // Check if enemy is close enough to attack (distance 0 or 1)
    const enemyTile = enemy.position.toTile(32);
    const distanceToTarget = Math.abs(enemyTile.x - enemy.blockBreakerTarget.x) + 
                             Math.abs(enemyTile.y - enemy.blockBreakerTarget.y);
    
    if (distanceToTarget > 1) {
      // Enemy is too far away - pathfind to building
      console.log(`[ATTACK] Enemy ${enemy.id} too far (${distanceToTarget}), pathfinding to building`);
      if (enemy.path.length === 0 || enemy.currentPathIndex >= enemy.path.length) {
        const path = this.pathfinder.findPath(
          new Vector2(enemyTile.x, enemyTile.y),
          enemy.blockBreakerTarget,
          state.grid
        );
        if (path.length > 0) {
          enemy.path = path.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
          enemy.currentPathIndex = 0;
          enemy.lastPosition = enemy.position.clone();
        }
      }
      return; // Too far to attack
    }
    
    console.log(`[ATTACK] Enemy ${enemy.id} close enough (${distanceToTarget}), checking cooldown=${(enemy.attackCooldown || 0).toFixed(2)}`);
    
    // Update attack cooldown
    enemy.attackCooldown = (enemy.attackCooldown || 0) - deltaTime;
    
    if ((enemy.attackCooldown || 0) <= 0) {
      // Attack the building
      const targetTile = enemy.blockBreakerTarget;
      const targetX = targetTile.x;
      const targetY = targetTile.y;
      
      // Find the building (tower or wall)
      let building: Tower | Wall | null = null;
      let buildingType: 'tower' | 'wall' | null = null;
      
      // Check towers
      for (const tower of state.towers) {
        const towerTile = new Vector2(Math.floor(tower.position.x), Math.floor(tower.position.y));
        if (towerTile.x === targetX && towerTile.y === targetY) {
          building = tower;
          buildingType = 'tower';
          break;
        }
      }
      
      // Check walls if not found in towers
      if (!building) {
        for (const wall of state.walls) {
          const wallTile = new Vector2(Math.floor(wall.position.x), Math.floor(wall.position.y));
          if (wallTile.x === targetX && wallTile.y === targetY) {
            building = wall;
            buildingType = 'wall';
            break;
          }
        }
      }
      
      if (building && buildingType) {
        // Deal damage (bosses deal 10, others deal 5)
        const damage = enemy.type === 'boss' ? 10 : 5;
        const oldHp = building.hp;
        building.hp -= damage;
        
        console.log(`[ATTACK] Enemy ${enemy.id} attacked ${buildingType} at (${targetX}, ${targetY}): ${oldHp} -> ${building.hp} HP`);
        
        // Play attack sound
        this.audioSystem?.playSound('tower_arrow_fire'); // Reuse tower fire sound
        
        // Reset cooldown (1 second)
        enemy.attackCooldown = 1.0;
        
        // Check if building is destroyed
        if (building.hp <= 0) {
          console.log(`[ATTACK] Building destroyed!`);
          this.destroyBuilding(buildingType, building, state);
        } else {
          // Update UI if this building is selected
          if (this.selectedTower && buildingType === 'tower' && building.id === this.selectedTower.id) {
            const tower = building as Tower;
            const canAffordA = this.economySystem.canAfford(UPGRADE_A_STATS[tower.type].cost);
            const canAffordB = this.economySystem.canAfford(UPGRADE_B_STATS[tower.type].cost);
            const canAffordC = (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper')
              ? this.economySystem.canAfford(UPGRADE_C_STATS[tower.type].cost)
              : false;
            this.towerInfoPanel.refresh(tower, canAffordA, canAffordB, canAffordC, false);
          } else if (this.selectedWall && buildingType === 'wall' && building.id === this.selectedWall.id) {
            // Wall HP updated - refresh display if wall is selected
            // Note: refreshWall method doesn't exist, so we'll just update the selected wall reference
            this.selectedWall = building as Wall;
          }
          
          // Redraw map to show updated HP
          this.needsMapRedraw = true;
          this.renderMap();
        }
      } else {
        // Building not found (might have been destroyed/sold) - exit block breaker mode
        console.log(`[ATTACK] Building not found at (${targetX}, ${targetY}) - exiting block breaker mode`);
        const enemyTypeName = enemy.type.charAt(0).toUpperCase() + enemy.type.slice(1);
        this.logMessages.addMessage(`Block Breaker: ${enemyTypeName} returning to normal path (target destroyed)`);
        enemy.isBlockBreaker = false;
        enemy.blockBreakerTarget = null;
        enemy.attackCooldown = 0;
        
        // Restore original speed
        enemy.speed = enemy.baseSpeed;
        console.log(`[BLOCK] Enemy ${enemy.id} exiting block breaker mode, speed restored to ${enemy.speed.toFixed(2)}`);
      }
    }
  }

  private destroyBuilding(type: 'wall' | 'tower', building: Wall | Tower, state: any): void {
    const tileX = type === 'wall' 
      ? Math.floor(building.position.x) 
      : Math.floor(building.position.x);
    const tileY = type === 'wall' 
      ? Math.floor(building.position.y) 
      : Math.floor(building.position.y);
    
    const buildingTile = new Vector2(tileX, tileY);
    
    // Play destruction sound
    this.audioSystem?.playSound('building_destroyed', 0.8);
    
    // Remove from blocking buildings list
    this.removeBlockingBuilding(buildingTile);
    
    // Remove building from state
    if (type === 'wall') {
      const wallIndex = state.walls.findIndex((w: Wall) => w.id === building.id);
      if (wallIndex >= 0) state.walls.splice(wallIndex, 1);
    } else {
      this.towerSystem.removeTower(building.id);
      // Remove MVP indicator if present
      const mvpIndicator = this.mvpIndicators.get(building.id);
      if (mvpIndicator) {
        this.dynamicGraphics.removeChild(mvpIndicator);
        this.mvpIndicators.delete(building.id);
      }
      this.towerDamageMap.delete(building.id);
      const towerIndex = state.towers.findIndex((t: Tower) => t.id === building.id);
      if (towerIndex >= 0) state.towers.splice(towerIndex, 1);
    }
    
    // Clear the tile
    if (tileY >= 0 && tileY < state.grid.length && 
        tileX >= 0 && tileX < state.grid[tileY].length) {
      state.grid[tileY][tileX].type = 'empty';
    }
    
    // Update path
    this.pathfinder.clearCache();
    this.updateGlobalPath(); // This will recalculate blocking buildings
    
    // Exit block breaker mode for enemies targeting this building
    this.exitBlockBreakerForBuilding(buildingTile);
    
    this.forceEnemyRepath();
    this.needsMapRedraw = true;
    this.renderMap();
    
    const buildingTypeName = type === 'wall' ? 'Wall' : 'Tower';
    this.logMessages.addMessage(`${buildingTypeName} destroyed by enemies`);
  }

  private removeBlockingBuilding(buildingTile: Vector2): void {
    const blockingIndex = this.blockingBuildings.findIndex(b => 
      b.x === buildingTile.x && b.y === buildingTile.y
    );
    if (blockingIndex >= 0) {
      this.blockingBuildings.splice(blockingIndex, 1);
      console.log(`Removed building at (${buildingTile.x}, ${buildingTile.y}) from blocking buildings list`);
    }
  }

  private exitBlockBreakerForBuilding(buildingTile: Vector2): void {
    // Exit block breaker mode for enemies targeting this building
    for (const enemy of this.enemySystem.getAllEnemies()) {
      if (enemy.isBlockBreaker && enemy.blockBreakerTarget &&
          enemy.blockBreakerTarget.x === buildingTile.x &&
          enemy.blockBreakerTarget.y === buildingTile.y) {
        const enemyTypeName = enemy.type.charAt(0).toUpperCase() + enemy.type.slice(1);
        
        // Check if enemy is at another blocking building
        const enemyTile = enemy.position.toTile(32);
        let closestBlockingBuilding: Vector2 | null = null;
        let minDistance = Infinity;
        
        for (const blockingBuilding of this.blockingBuildings) {
          const distance = Math.abs(enemyTile.x - blockingBuilding.x) + 
                           Math.abs(enemyTile.y - blockingBuilding.y);
          if (distance <= 1 && distance < minDistance) {
            // Enemy is at or adjacent to another blocking building
            closestBlockingBuilding = blockingBuilding;
            minDistance = distance;
          }
        }
        
        if (closestBlockingBuilding) {
          // Enemy is already at another blocking building - immediately switch target!
          enemy.blockingBuildingTile = closestBlockingBuilding.clone();
          enemy.blockBreakerTarget = closestBlockingBuilding.clone();
          enemy.attackCooldown = 0; // Reset cooldown for new target
          
          console.log(`[BLOCK] Enemy ${enemy.id} immediately switching to next building at (${closestBlockingBuilding.x}, ${closestBlockingBuilding.y})`);
          this.logMessages.addMessage(
            `Block Breaker: ${enemyTypeName} attacking next building`
          );
        } else {
          // Not at another blocking building - clear all state
          enemy.isBlockBreaker = false;
          enemy.blockBreakerTarget = null;
          enemy.attackCooldown = 0;
          enemy.blockingBuildingTile = null;
          
          // Restore original speed
          enemy.speed = enemy.baseSpeed;
          
          console.log(`[BLOCK] Enemy ${enemy.id} not at another blocking building, fully exiting block breaker mode, speed restored to ${enemy.speed.toFixed(2)}`);
          this.logMessages.addMessage(
            `Block Breaker: ${enemyTypeName} returning to normal path`
          );
        }
        
        // Clear path to force recalculation (for all cases)
        enemy.path = [];
        enemy.currentPathIndex = 0;
      }
    }
    
    // Also clear state for enemies that are NOT in block breaker mode but were targeting this building
    for (const enemy of this.enemySystem.getAllEnemies()) {
      if (!enemy.isBlockBreaker && enemy.blockingBuildingTile &&
          enemy.blockingBuildingTile.x === buildingTile.x &&
          enemy.blockingBuildingTile.y === buildingTile.y) {
        console.log(`[BLOCK] Clearing blockingBuildingTile for non-breaker enemy ${enemy.id}`);
        enemy.blockingBuildingTile = null;
        enemy.path = [];
        enemy.currentPathIndex = 0;
      }
    }
  }

  private addTestGold(): void {
    const state = this.gameState.getState();
    this.economySystem.addGold(500);
    this.logMessages.addMessage('+500 gold (test)');
  }

  private spawnTestEnemies(type: EnemyType, count: number): void {
    const state = this.gameState.getState();
    if (state.spawns.length === 0) {
      this.logMessages.addMessage('No spawn point available');
      return;
    }

    const spawn = state.spawns[0];
    const waveNumber = this.gameState.getWaveNumber();
    const isSurvival = this.gameState.isSurvivalMode();

    // Spawn enemies with 0.4 second (400ms) interval
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        const enemy = this.enemySystem.createEnemy(
          type,
          spawn.clone(),
          waveNumber,
          isSurvival,
          false // No elite for test spawns
        );
        this.audioSystem?.playSound('enemy_spawn');

        // Set path - ghost gets direct path, flying moves directly (no path), others get global path
        if (type === 'ghost') {
          const enemyTile = enemy.position.toTile(32);
          const goal = state.goals[0];
          const goalTile = goal.toTile(32);
          const path = this.pathfinder.findPath(
            new Vector2(enemyTile.x, enemyTile.y),
            new Vector2(goalTile.x, goalTile.y),
            state.grid,
            true // ignoreBuildings = true
          );
          
          if (path.length > 0) {
            enemy.path = path.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
            let bestIndex = 0;
            let minDist = Infinity;
            for (let j = 0; j < enemy.path.length; j++) {
              const dist = enemy.position.distance(enemy.path[j]);
              if (dist < minDist) {
                minDist = dist;
                bestIndex = j;
              }
            }
            if (minDist < 1.0 && bestIndex < enemy.path.length - 1) {
              bestIndex = bestIndex + 1;
            }
            enemy.currentPathIndex = bestIndex;
            enemy.lastPosition = enemy.position.clone();
          }
        } else if (type === 'flying') {
          // Flying enemies: No path needed - they move directly towards goal in EnemySystem
          enemy.path = [];
          enemy.currentPathIndex = 0;
          enemy.lastPosition = enemy.position.clone();
        } else if (this.globalPath.length > 0) {
          enemy.path = this.globalPath.map(p => p.clone());
          enemy.currentPathIndex = 0;
          enemy.lastPosition = enemy.position.clone();
        }
      }, i * 400); // 0.4 second (400ms) interval between spawns
    }

    const typeName = type.charAt(0).toUpperCase() + type.slice(1);
    this.logMessages.addMessage(`Spawned ${count} ${typeName} enemies`);
  }

  private spawnTestBoss(): void {
    const state = this.gameState.getState();
    if (state.spawns.length === 0) {
      this.logMessages.addMessage('No spawn point available');
      return;
    }

    const spawn = state.spawns[0];
    const waveNumber = 10; // Level 10 boss
    const isSurvival = this.gameState.isSurvivalMode();

    // Create boss enemy
    const boss = this.enemySystem.createEnemy(
      'boss',
      spawn.clone(),
      waveNumber,
      isSurvival,
      false // Not elite (bosses are already special)
    );
    this.audioSystem?.playSound('enemy_spawn');

    // Set path using global path (already in pixel coordinates)
    if (this.globalPath.length > 0) {
      boss.path = this.globalPath.map(p => p.clone());
      boss.currentPathIndex = 0;
      boss.lastPosition = boss.position.clone();
    }

    this.logMessages.addMessage(`Spawned level ${waveNumber} boss (${Math.ceil(boss.hp)} HP)`);
  }
}


